/**
 * AchatsTab — Onglet Achats & Dépenses extrait de CaissePage.jsx.
 *
 * Reçoit un `ctx` contenant tous les state + handlers requis. Ce composant est
 * purement "présentation" : toute la logique métier (fetch, create, update, revise,
 * convertToPO, print, modals) reste côté parent CaissePage.jsx.
 *
 * Ainsi l'extraction ne change rien au comportement existant (itérations 42-49).
 */
import React from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShoppingCart, Plus, Eye, CheckCircle, AlertCircle, Edit2, Trash2,
  FileText, Printer, Receipt, Calendar, X, Truck, Wallet, Ban, Wine, PackagePlus,
  MoreVertical, SlidersHorizontal, ArrowUpDown, LayoutList,
} from "lucide-react";
import ExpenseAnalysisBadges from "./ExpenseAnalysisBadges";
import DrinkPurchaseDialog from "./DrinkPurchaseDialog";
import PurchasePriceHistoryTab from "./PurchasePriceHistoryTab";
import ExpensesByProductTab from "./ExpensesByProductTab";
import AchatsManagerPanels from "./AchatsManagerPanels";
import ArchiveAllPurchasesButton from "./ArchiveAllPurchasesButton";
import ArchivePastMonthsButton, { useAutoArchivePastMonths } from "./ArchivePastMonthsButton";
import AssignDateDialog from "./AssignDateDialog";
import ArchivedExpensesView from "./ArchivedExpensesView";
import ExpenseCompactList from "./ExpenseCompactList";
import AchatsDashboard from "./AchatsDashboard";
import AchatsToolbar from "./AchatsToolbar";

const STRIKE_REASONS = [
  { value: "pas_opportun", label: "Pas opportun" },
  { value: "a_reporter", label: "À reporter" },
  { value: "a_abandonner", label: "À abandonner" },
  { value: "autres", label: "Autres" },
];

const STRIKE_LABEL = STRIKE_REASONS.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

// Type & Destination badges (29/04/2026)
const DEST_LABELS = {
  cuisine: "🍳 Cuisine",
  bar: "🍹 Bar",
  salle: "🪑 Salle",
  jeux_vr: "🎮 Jeux VR",
  jardin: "🌳 Jardin",
  administratif: "📋 Administratif",
};

export const TypeDestBadges = ({ expense, compact = false }) => {
  if (!expense) return null;
  const t = expense.expense_type;
  const d = expense.destination;
  if (!t && !d) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${compact ? "" : "ml-1"}`}>
      {t && (
        <Badge className={`text-[10px] ${
          t === "paiement"
            ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
            : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
        }`}>
          {t === "paiement" ? "💳 Paiement" : "🛒 Achat"}
        </Badge>
      )}
      {d && (
        <Badge className="text-[10px] bg-slate-700/50 text-slate-300 border border-slate-600">
          {DEST_LABELS[d] || d}
        </Badge>
      )}
    </span>
  );
};

/**
 * Compare original_items (manager's submission snapshot) with current items
 * (final corrected version) and return a structured audit trail.
 *
 * Returns: { added[], removed[], modified[], struck[], unchangedCount, hasChanges }
 *
 * Matching is done by normalized description (lowercase + trimmed). When an item's
 * description was edited by admin, we treat it as removed+added pair.
 */
const computeAuditTrail = (expense) => {
  const originals = expense?.original_items || [];
  const finals = expense?.items || [];
  if (!expense?.original_items) {
    return { added: [], removed: [], modified: [], struck: [], unchangedCount: 0, hasChanges: false };
  }
  const norm = (s) => String(s || "").trim().toLowerCase();
  const finalsByDesc = new Map();
  finals.forEach((it, i) => {
    const k = norm(it.description);
    if (!finalsByDesc.has(k)) finalsByDesc.set(k, []);
    finalsByDesc.get(k).push({ ...it, _i: i });
  });
  const added = [];
  const removed = [];
  const modified = [];
  const struck = [];
  let unchangedCount = 0;

  const matchedFinalIdx = new Set();
  originals.forEach((orig) => {
    const k = norm(orig.description);
    const candidates = finalsByDesc.get(k) || [];
    const match = candidates.find((c) => !matchedFinalIdx.has(c._i));
    if (!match) {
      removed.push(orig);
      return;
    }
    matchedFinalIdx.add(match._i);
    if (match.struck) {
      struck.push({ ...match, original: orig });
      return;
    }
    const qtyChanged = Number(match.quantity) !== Number(orig.quantity);
    const puChanged = Number(match.unit_price) !== Number(orig.unit_price);
    if (qtyChanged || puChanged) {
      modified.push({ original: orig, current: match, qtyChanged, puChanged });
    } else {
      unchangedCount += 1;
    }
  });
  finals.forEach((it, i) => {
    if (!matchedFinalIdx.has(i)) {
      // New line added by admin (not present in originals)
      if (!it.struck) added.push(it);
    }
  });
  const hasChanges = added.length > 0 || removed.length > 0 || modified.length > 0 || struck.length > 0;
  return { added, removed, modified, struck, unchangedCount, hasChanges };
};

const AchatsTab = ({ ctx }) => {
  const [showDrinksPurchase, setShowDrinksPurchase] = React.useState(false);
  const {
    currentUser,
    expenses: _rawExpenses,
    shoppingList,
    achatsSubView, setAchatsSubView,
    showAllExpenses, setShowAllExpenses,
    expenseRatioAlert,
    expenseAnalyses,
    formatPrice,
    setShowExpenseModal,
    setShowShoppingListModal,
    setExpenseToAssign,
    setShowWeekAssignModal,
    printExpensesTicket,
    printAllExpensesList,
    printAllApprovedExpenses,
    printApprovedExpensesDetailed,
    printCompletedExpensesTicket,
    printAllCompletedExpenses,
    printSingleExpenseTicket,
    printExpensePDF,
    openExpenseForEdit,
    deleteExpense,
    updateExpense,
    openReviseModal,
    convertExpenseToPO,
    availableAccounts,
    allocateExpenseToAccount,
    fetchExpenses,
    receiveExpenseStock,
  } = ctx;

  // 🆕 Toolbar filter — si l'utilisateur active recherche/filtres, on limite la liste
  const [filteredIds, setFilteredIds] = React.useState(null);
  // 🆕 Filtre de date issu du picker AchatsDashboard — conservé pour le TITRE
  // uniquement, PAS pour filtrer la liste (l'onglet "Historique" doit toujours
  // montrer tous les achats indépendamment du mois affiché dans les KPI).
  const [dashboardDateFilter, setDashboardDateFilter] = React.useState(null);
  const expenses = React.useMemo(() => {
    const base = _rawExpenses || [];
    if (!filteredIds) return base;
    const set = new Set(filteredIds);
    return base.filter(e => set.has(e.id));
  }, [_rawExpenses, filteredIds]);

  // Local state for "rayer une ligne" edits per pending grouped expense.
  // Shape: { [expenseId]: [ { ...item, struck, strike_reason, strike_note } ] }
  const [strikeEdits, setStrikeEdits] = React.useState({});

  // 🆕 Auto-archivage des achats des mois terminés (1×/jour, admin/manager)
  useAutoArchivePastMonths(currentUser, fetchExpenses);

  // Mode sélection multiple pour rattachement de période (admin uniquement)
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [showAssignDateDialog, setShowAssignDateDialog] = React.useState(false);

  // Mode d'affichage compact des achats (lignes avec tiret cliquables).
  // Par défaut : compact. Désactivable pour revenir aux cartes détaillées.
  const [compactMode, setCompactMode] = React.useState(true);
  const [expandedExpenseId, setExpandedExpenseId] = React.useState(null);

  const isCompactView = compactMode && achatsSubView !== 'rejetes'
    && achatsSubView !== 'repertoire_prix'
    && achatsSubView !== 'par_produit'
    && achatsSubView !== 'archives';

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Toggle which list to display for admin_review expenses: 'original' | 'corrected'.
  // Defaults to 'corrected' for admin (their work-in-progress) and 'original' for manager.
  const [reviewViewMode, setReviewViewMode] = React.useState({});
  const getReviewViewMode = (expenseId, defaultMode) =>
    reviewViewMode[expenseId] || defaultMode;
  const setReviewViewModeFor = (expenseId, mode) =>
    setReviewViewMode((prev) => ({ ...prev, [expenseId]: mode }));

  // Author filter for "Validation en cours" (admin only) : 'all' | 'admin' | 'manager' | 'other'
  const [authorFilter, setAuthorFilter] = React.useState('all');

  // UI : menu overflow (⋯) mobile + filtres avancés collapsibles
  const [showActionMenu, setShowActionMenu] = React.useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);
  // Tri global des listes : date_desc | date_asc | name_asc | name_desc | amount_desc | amount_asc
  const [sortBy, setSortBy] = React.useState('date_desc');

  /** Tri des items à l'intérieur d'une dépense (alphabétique A→Z). */
  const sortItems = React.useCallback((items) => {
    const arr = Array.isArray(items) ? [...items] : [];
    return arr.sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'fr', { sensitivity: 'base' }));
  }, []);

  /** Tri global appliqué à un tableau d'expenses. */
  const sortExpenses = React.useCallback((arr) => {
    const list = Array.isArray(arr) ? [...arr] : [];
    const cmp = (a, b) => {
      switch (sortBy) {
        case 'name_asc':   return (a.title || a.description || '').localeCompare(b.title || b.description || '', 'fr', { sensitivity: 'base' });
        case 'name_desc':  return (b.title || b.description || '').localeCompare(a.title || a.description || '', 'fr', { sensitivity: 'base' });
        case 'amount_desc': return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        case 'amount_asc':  return (Number(a.amount) || 0) - (Number(b.amount) || 0);
        case 'date_asc':   return (a.created_at || '').localeCompare(b.created_at || '');
        case 'date_desc':
        default:           return (b.created_at || '').localeCompare(a.created_at || '');
      }
    };
    return list.sort(cmp);
  }, [sortBy]);

  // Helpers : qu'est-ce qu'une dépense "terminée" ?
  //  - status === 'completed' : achat normal réglé.
  //  - category === 'paiement' && is_paid === true : prestation marquée payée.
  // Ces deux cas doivent disparaître du profil de la Responsable Op. & Log (déjà réglés/payés)
  // et apparaître dans l'onglet "Achats terminés" côté Admin.
  const isFinished = (e) =>
    e.status === 'completed' ||
    (e.category === 'paiement' && e.is_paid === true);
  const isAdminUser = currentUser?.role === 'admin';
  // List of unique authors found in current expenses (for the dropdown of "other")
  const allAuthors = React.useMemo(() => {
    const set = new Set();
    (expenses || []).forEach(e => { if (e.requested_by) set.add(e.requested_by); });
    return Array.from(set);
  }, [expenses]);

  const matchesAuthorFilter = (expense) => {
    if (authorFilter === 'all') return true;
    const author = (expense.requested_by || '').toLowerCase();
    if (authorFilter === 'admin') {
      return author.includes('admin') || author.includes('administrateur');
    }
    if (authorFilter === 'manager') {
      return author.includes('manager') || author.includes('responsable op. & log') || author.includes('gerante');
    }
    if (authorFilter === 'other') {
      return !author.includes('admin') && !author.includes('administrateur')
          && !author.includes('manager') && !author.includes('responsable op. & log') && !author.includes('gerante');
    }
    // Specific named filter (e.g., a precise full_name)
    return expense.requested_by === authorFilter;
  };

  const getEditedItems = (expense) => {
    if (strikeEdits[expense.id]) return strikeEdits[expense.id];
    return (expense.items || []).map((it) => ({
      ...it,
      struck: !!it.struck,
      strike_reason: it.strike_reason || "",
      strike_note: it.strike_note || "",
    }));
  };

  const updateStrikeItem = (expenseId, idx, patch) => {
    setStrikeEdits((prev) => {
      const current = prev[expenseId] ? [...prev[expenseId]] : (
        (expenses.find((e) => e.id === expenseId)?.items || []).map((it) => ({
          ...it,
          struck: !!it.struck,
          strike_reason: it.strike_reason || "",
          strike_note: it.strike_note || "",
        }))
      );
      current[idx] = { ...current[idx], ...patch };
      return { ...prev, [expenseId]: current };
    });
  };

  const handleAdminFirstValidation = (expense) => {
    const adminAmountInput = document.getElementById(`admin-amount-${expense.id}`);
    const editedItems = getEditedItems(expense);
    // First validation: status -> admin_review (stays in admin's profile)
    const payload = { status: "admin_review", approved_by: "Administrateur" };

    if (expense.is_group && editedItems.length > 0) {
      payload.items = editedItems.map((it) => ({
        category: it.category,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
        amount: it.amount,
        struck: !!it.struck,
        strike_reason: it.struck ? (it.strike_reason || "autres") : null,
        strike_note: it.struck ? (it.strike_note || "") : null,
      }));
    } else if (adminAmountInput) {
      payload.amount = parseFloat(adminAmountInput.value) || expense.amount;
    }
    updateExpense(expense.id, payload);
  };

  // Direct approval: skip admin_review entirely, go straight to "approved"
  // (use this when the admin has nothing to modify in the manager's list).
  const handleApproveDirectly = (expense) => {
    const editedItems = getEditedItems(expense);
    const hasStruck = expense.is_group && editedItems.some((it) => it.struck);
    const baseMsg = `Valider directement cette demande sans la modifier ?\n\n${expense.description}\n` +
      (expense.is_group ? `(${editedItems.filter(it => !it.struck).length} articles)` : "");
    const warn = hasStruck
      ? "\n\n⚠ Vous avez coché des lignes à rayer — elles seront prises en compte. Pour modifier davantage, utilisez plutôt 'Première validation'."
      : "\n\nLa liste est envoyée à la responsable op. & log telle quelle.";
    if (!window.confirm(baseMsg + warn)) return;

    const adminAmountInput = document.getElementById(`admin-amount-${expense.id}`);
    const payload = { status: "approved", approved_by: "Administrateur" };
    if (expense.is_group && editedItems.length > 0) {
      payload.items = editedItems.map((it) => ({
        category: it.category,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
        amount: it.amount,
        struck: !!it.struck,
        strike_reason: it.struck ? (it.strike_reason || "autres") : null,
        strike_note: it.struck ? (it.strike_note || "") : null,
      }));
    } else if (adminAmountInput) {
      payload.amount = parseFloat(adminAmountInput.value) || expense.amount;
    }
    updateExpense(expense.id, payload);
  };

  const handleSendToManager = (expense) => {
    if (!window.confirm(`Envoyer cette liste corrigée à la responsable op. & log pour achat ?\n\n${expense.description}`)) return;
    const editedItems = getEditedItems(expense);
    const payload = { status: "approved", approved_by: "Administrateur" };
    if (expense.is_group && editedItems.length > 0) {
      payload.items = editedItems.map((it) => ({
        category: it.category,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
        amount: it.amount,
        struck: !!it.struck,
        strike_reason: it.struck ? (it.strike_reason || "autres") : null,
        strike_note: it.struck ? (it.strike_note || "") : null,
      }));
    }
    updateExpense(expense.id, payload);
  };

  const handleSaveAdminReviewEdits = (expense) => {
    const editedItems = getEditedItems(expense);
    if (!expense.is_group || editedItems.length === 0) return;
    updateExpense(expense.id, {
      status: "admin_review",
      items: editedItems.map((it) => ({
        category: it.category,
        description: it.description,
        quantity: parseFloat(it.quantity) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        amount: (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
        struck: !!it.struck,
        strike_reason: it.struck ? (it.strike_reason || "autres") : null,
        strike_note: it.struck ? (it.strike_note || "") : null,
      })),
    });
  };

  const updateAdminReviewItem = (expense, idx, patch) => {
    setStrikeEdits((prev) => {
      const current = prev[expense.id] ? [...prev[expense.id]] : (expense.items || []).map((it) => ({ ...it }));
      const next = { ...current[idx], ...patch };
      if ("quantity" in patch || "unit_price" in patch) {
        const q = parseFloat(next.quantity) || 0;
        const p = parseFloat(next.unit_price) || 0;
        next.amount = q * p;
      }
      current[idx] = next;
      return { ...prev, [expense.id]: current };
    });
  };

  const addAdminReviewItem = (expense) => {
    setStrikeEdits((prev) => {
      const current = prev[expense.id] ? [...prev[expense.id]] : (expense.items || []).map((it) => ({ ...it }));
      current.push({
        category: expense.category || "autres",
        description: "",
        quantity: 0,
        unit_price: 0,
        amount: 0,
        struck: false,
        strike_reason: "",
        strike_note: "",
      });
      return { ...prev, [expense.id]: current };
    });
  };

  const removeAdminReviewItem = (expense, idx) => {
    setStrikeEdits((prev) => {
      const current = prev[expense.id] ? [...prev[expense.id]] : (expense.items || []).map((it) => ({ ...it }));
      current.splice(idx, 1);
      return { ...prev, [expense.id]: current };
    });
  };

  return (
            <div className="ach-root space-y-3">
              <style>{`
                .ach-root, .ach-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
                .ach-title { font-size: 18px; font-weight: 800; letter-spacing: -0.02em; color: white; }
                @media (min-width: 640px) { .ach-title { font-size: 24px; } }
                @media (min-width: 1024px) { .ach-title { font-size: 28px; } }
                .ach-subtitle {
                  font-size: 10.5px; font-weight: 600; color: rgb(148, 163, 184);
                  text-transform: uppercase; letter-spacing: 0.12em;
                  margin-top: 2px;
                }
                @media (min-width: 640px) { .ach-subtitle { font-size: 12px; letter-spacing: 0.14em; } }
                .ach-chip {
                  display: inline-flex; align-items: center; gap: 5px;
                  padding: 4px 10px; border-radius: 999px;
                  font-size: 11px; font-weight: 700;
                  background: rgba(51, 65, 85, 0.6);
                  border: 1px solid rgba(71, 85, 105, 0.5);
                  color: rgb(226, 232, 240);
                }
                @media (min-width: 640px) { .ach-chip { padding: 5px 12px; font-size: 12px; gap: 6px; } }
                .ach-cta {
                  height: 36px; padding: 0 14px;
                  border-radius: 10px;
                  background: linear-gradient(135deg, rgb(147, 51, 234), rgb(126, 34, 206));
                  color: white; font-weight: 700; font-size: 12.5px;
                  display: inline-flex; align-items: center; gap: 6px;
                  transition: transform 140ms, box-shadow 140ms;
                  box-shadow: 0 4px 12px -4px rgba(147, 51, 234, 0.6);
                  flex-shrink: 0;
                }
                @media (min-width: 640px) { .ach-cta { height: 40px; padding: 0 18px; border-radius: 12px; font-size: 13.5px; gap: 8px; } }
                .ach-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 20px -6px rgba(147, 51, 234, 0.75); }
                .ach-icon-btn {
                  width: 36px; height: 36px; border-radius: 10px;
                  display: flex; align-items: center; justify-content: center;
                  background: rgba(30, 41, 59, 0.7);
                  border: 1px solid rgba(71, 85, 105, 0.55);
                  color: rgb(203, 213, 225);
                  transition: background-color 140ms, color 140ms;
                  flex-shrink: 0;
                }
                @media (min-width: 640px) { .ach-icon-btn { width: 40px; height: 40px; border-radius: 12px; } }
                .ach-icon-btn:hover { background: rgba(51, 65, 85, 0.9); color: white; }
                .ach-mtab {
                  padding: 9px 16px; border-radius: 999px;
                  font-size: 13px; font-weight: 700;
                  background: transparent; color: rgb(148, 163, 184);
                  border: 1px solid rgba(71, 85, 105, 0.4);
                  transition: all 160ms; white-space: nowrap; flex-shrink: 0;
                  display: inline-flex; align-items: center; gap: 6px;
                }
                .ach-mtab:hover { color: white; background: rgba(51,65,85,0.4); }
                .ach-mtab[data-active="true"] {
                  background: color-mix(in srgb, var(--tabc) 18%, transparent);
                  color: var(--tabc);
                  border-color: color-mix(in srgb, var(--tabc) 40%, transparent);
                }
                .ach-mtab-count {
                  min-width: 20px; height: 20px; padding: 0 6px;
                  border-radius: 6px;
                  background: rgba(15, 23, 42, 0.7);
                  color: currentColor;
                  font-family: 'JetBrains Mono', monospace;
                  font-size: 11px; font-weight: 700;
                  line-height: 20px; text-align: center;
                }
                .ach-pill {
                  padding: 4px 10px; border-radius: 999px;
                  font-size: 11px; font-weight: 600;
                  background: rgba(30, 41, 59, 0.5); color: rgb(148, 163, 184);
                  border: 1px solid rgba(71, 85, 105, 0.4);
                  transition: all 140ms; flex-shrink: 0;
                }
                .ach-pill:hover { color: white; background: rgba(51,65,85,0.6); }
                .ach-pill[data-active="true"] {
                  background: rgb(245, 158, 11); color: rgb(15, 23, 42);
                  border-color: rgb(245, 158, 11); font-weight: 700;
                }
                .ach-kpi {
                  border-radius: 14px; padding: 12px 14px;
                  background: linear-gradient(135deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.6));
                  border: 1px solid rgba(71, 85, 105, 0.5);
                  border-left: 4px solid rgba(148, 163, 184, 0.6);
                  text-align: left; display: block;
                  transition: transform 140ms, background-color 140ms, border-color 140ms;
                  min-width: 0;
                }
                @media (min-width: 640px) { .ach-kpi { padding: 16px 18px; } }
                .ach-kpi:hover { transform: translateY(-2px); background: linear-gradient(135deg, rgba(51, 65, 85, 0.8), rgba(30, 41, 59, 0.7)); }
                .ach-kpi[data-tone="primary"]   { border-left-color: rgb(59, 130, 246); }
                .ach-kpi[data-tone="warning"]   { border-left-color: rgb(245, 158, 11); }
                .ach-kpi[data-tone="danger"]    { border-left-color: rgb(239, 68, 68); }
                .ach-kpi[data-tone="success"]   { border-left-color: rgb(16, 185, 129); }
                .ach-kpi[data-tone="purple"]    { border-left-color: rgb(168, 85, 247); }
                .ach-kpi[data-tone="cyan"]      { border-left-color: rgb(34, 211, 238); }
                .ach-kpi[data-emphasis="strong"] {
                  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.85));
                  border-color: rgba(100, 116, 139, 0.65);
                  padding: 14px 16px;
                }
                @media (min-width: 640px) { .ach-kpi[data-emphasis="strong"] { padding: 18px 20px; } }
                .ach-kpi-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.13em; text-transform: uppercase; color: rgb(148, 163, 184); }
                .ach-kpi-value {
                  font-size: 18px; font-weight: 800; color: white; font-family: 'JetBrains Mono', monospace;
                  margin-top: 4px; line-height: 1.1; letter-spacing: -0.02em;
                  white-space: nowrap;
                  overflow: hidden; text-overflow: ellipsis;
                }
                @media (min-width: 640px) { .ach-kpi-value { font-size: 26px; margin-top: 6px; } }
                .ach-kpi[data-emphasis="strong"] .ach-kpi-value { font-size: 22px; }
                @media (min-width: 640px) { .ach-kpi[data-emphasis="strong"] .ach-kpi-value { font-size: 32px; } }
                .ach-kpi[data-emphasis="soft"] .ach-kpi-value { font-size: 16px; }
                @media (min-width: 640px) { .ach-kpi[data-emphasis="soft"] .ach-kpi-value { font-size: 20px; } }
                .ach-kpi-sub { font-size: 11px; color: rgb(148, 163, 184); margin-top: 3px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                @media (min-width: 640px) { .ach-kpi-sub { font-size: 12px; margin-top: 4px; } }
                .ach-sticky-bulk {
                  position: sticky; bottom: 12px;
                  background: rgba(147, 51, 234, 0.95);
                  backdrop-filter: blur(8px);
                  box-shadow: 0 8px 24px -8px rgba(147, 51, 234, 0.55), 0 2px 6px rgba(0,0,0,0.4);
                  border-radius: 14px;
                  padding: 10px 14px;
                  z-index: 20;
                  display: flex; align-items: center; gap: 10px;
                  flex-wrap: wrap;
                }
                .ach-menu {
                  position: absolute; right: 0; top: calc(100% + 6px);
                  min-width: 220px;
                  background: rgb(15, 23, 42);
                  border: 1px solid rgb(51, 65, 85);
                  border-radius: 12px;
                  padding: 6px;
                  box-shadow: 0 12px 28px -10px rgba(0,0,0,0.6);
                  z-index: 30;
                }
                .ach-menu-item {
                  width: 100%; display: flex; align-items: center; gap: 10px;
                  padding: 8px 10px; border-radius: 8px;
                  font-size: 13px; font-weight: 600;
                  color: rgb(203, 213, 225); text-align: left;
                  transition: background-color 120ms;
                }
                .ach-menu-item:hover { background: rgba(51, 65, 85, 0.7); color: white; }
              `}</style>

              {/* ═══ HEADER : titre + subtitle + counter + CTA + kebab ═══ */}
              <div className="flex items-start gap-3 flex-wrap">
                <div className="p-2.5 rounded-xl bg-purple-500/15 border border-purple-500/30 flex-shrink-0">
                  <ShoppingCart className="w-6 h-6 text-purple-300" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="ach-title truncate">Achats &amp; Dépenses</div>
                  <div className="ach-subtitle">
                    {(() => {
                      // 🆕 Titre synchronisé avec le picker de date du dashboard
                      const df = dashboardDateFilter;
                      if (df?.mode === "range" && df?.start_date && df?.end_date) {
                        return `Tableau de bord — ${df.start_date} → ${df.end_date}`;
                      }
                      if (df?.mode === "month" && df?.month) {
                        const [y, m] = df.month.split("-").map(Number);
                        if (y && m) {
                          const d = new Date(y, m - 1, 1);
                          return `Tableau de bord — ${d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;
                        }
                      }
                      return `Tableau de bord — ${new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;
                    })()}
                  </div>
                </div>
                {expenses.length > 0 && (
                  <span className="ach-chip" title="Nombre total de demandes">
                    <span className="font-mono">{expenses.length}</span>
                    <span className="text-slate-400 text-[10px] uppercase tracking-wider">demande{expenses.length > 1 ? 's' : ''}</span>
                  </span>
                )}
                {(currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowExpenseModal(true)}
                      className="ach-cta"
                      data-testid="new-expense-btn"
                      title={currentUser?.role === 'admin' ? "Enregistre un achat déjà effectué, sans passer par la file d'approbation" : "Créer une demande d'achat commune"}
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.6} />
                      <span className="hidden sm:inline">
                        {currentUser?.role === 'admin' ? "Achat direct (sans validation)" : "Achats communs"}
                      </span>
                      <span className="sm:hidden">
                        {currentUser?.role === 'admin' ? "Achat direct" : "Nouveau"}
                      </span>
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowActionMenu((s) => !s)}
                        className="ach-icon-btn"
                        title="Plus d'actions"
                        data-testid="achats-more-menu-btn"
                      >
                        <MoreVertical className="w-4 h-4" strokeWidth={2.4} />
                      </button>
                      {showActionMenu && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setShowActionMenu(false)} />
                          <div className="ach-menu">
                            <button
                              type="button"
                              onClick={() => { setShowDrinksPurchase(true); setShowActionMenu(false); }}
                              className="ach-menu-item"
                              data-testid="new-drinks-purchase-btn"
                            >
                              <Wine className="w-4 h-4 text-orange-400" strokeWidth={2.2} />
                              Achat Boissons
                            </button>
                            {currentUser?.role === 'manager' && (
                              <button
                                type="button"
                                onClick={() => { setShowShoppingListModal(true); setShowActionMenu(false); }}
                                className="ach-menu-item"
                              >
                                <ShoppingCart className="w-4 h-4 text-indigo-400" strokeWidth={2.2} />
                                Achats Fournisseurs {shoppingList.length > 0 && <span className="ml-auto text-[10px] font-mono bg-slate-700 px-1.5 rounded">{shoppingList.length}</span>}
                              </button>
                            )}
                            {(currentUser?.role === 'admin' || currentUser?.role === 'manager') && (
                              <div onClick={() => setShowActionMenu(false)}>
                                <ArchivePastMonthsButton
                                  currentUser={currentUser}
                                  onArchived={fetchExpenses}
                                />
                              </div>
                            )}
                            {currentUser?.role === 'admin' && (
                              <div onClick={() => setShowActionMenu(false)}>
                                <ArchiveAllPurchasesButton
                                  currentUser={currentUser}
                                  onArchived={fetchExpenses}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* 🆕 Dashboard KPI Achats (Feb 2026) — admin & manager
                  Le picker de date propage sa sélection au parent via `onDateFilterChange`
                  pour synchroniser la liste (À valider / Validés / Historique). */}
              {(currentUser?.role === 'admin' || currentUser?.role === 'manager') && (
                <AchatsDashboard onDateFilterChange={setDashboardDateFilter} />
              )}

              {/* 🆕 Toolbar recherche + filtres (Feb 2026) */}
              <AchatsToolbar
                expenses={_rawExpenses || []}
                onFilter={(list) => {
                  const total = (_rawExpenses || []).length;
                  if (list.length === total) setFilteredIds(null);
                  else setFilteredIds(list.map(e => e.id));
                }}
              />

              {/* ═══ ONGLETS PRINCIPAUX en pills — scroll horizontal sur mobile avec fade droit ═══ */}
              <div className="relative">
                <div
                  className="flex items-center gap-1.5 overflow-x-auto pb-1 pr-6 sm:pr-0"
                  style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
                >
                {(() => {
                  const groups = [
                    {
                      key: 'a_valider', label: 'À valider', color: '#a855f7',
                      sub: ['en_cours', 'a_reviser', ...(currentUser?.role === 'admin' ? ['mes_demandes'] : [])],
                      countFn: () => expenses.filter(e =>
                        e.status === 'pending' || e.status === 'admin_review' || e.status === 'revision_requested'
                      ).length,
                    },
                    {
                      key: 'valides_group', label: 'Validés', color: '#10b981',
                      sub: ['valides'],
                      countFn: () => expenses.filter(e => e.status === 'approved').length,
                    },
                    // 🗑 Fév 2026 — Onglets "Historique" (Rejetés, Répertoire prix, Archives) supprimés
                    // à la demande utilisateur. Le code backend reste actif mais l'accès UI est retiré.
                    ...(isAdminUser ? [{
                      key: 'achats_manager', label: 'Achats Manager', color: '#f59e0b',
                      sub: ['achete'],
                      countFn: () => expenses.filter(e =>
                        e.source === 'appro_manager' && e.payment_mode
                      ).length,
                    }] : []),
                  ];
                  const activeGroup = groups.find(g => g.sub.includes(achatsSubView)) || groups[0];
                  return groups.map((g) => {
                    const isActive = g.key === activeGroup.key;
                    const count = g.countFn();
                    return (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => setAchatsSubView(g.sub[0])}
                        data-active={isActive}
                        data-testid={`achats-maintab-${g.key}`}
                        className="ach-mtab"
                        style={{ "--tabc": g.color }}
                      >
                        <span>{g.label}</span>
                        {count > 0 && <span className="ach-mtab-count">{count}</span>}
                      </button>
                    );
                  });
                })()}
                </div>
                {/* Fade-out droit : indique que ça défile horizontalement */}
                <div className="sm:hidden pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-slate-900 to-transparent" />
              </div>

              {/* ═══ SOUS-FILTRES en pills (si applicable) ═══ */}
              {(() => {
                const subFilters = {
                  a_valider: [
                    { key: 'en_cours', label: 'En cours', count: expenses.filter(e => e.status === 'pending' || e.status === 'admin_review').length },
                    { key: 'a_reviser', label: 'À réviser', count: expenses.filter(e => e.status === 'revision_requested').length },
                    ...(currentUser?.role === 'admin' ? [{ key: 'mes_demandes', label: 'Mes demandes', count: expenses.filter(e => {
                      const me = currentUser?.full_name || currentUser?.username || '';
                      return e.requested_by === me || e.requested_by === currentUser?.username;
                    }).length }] : []),
                  ],
                  valides_group: [
                    { key: 'valides', label: 'Validés', count: expenses.filter(e => e.status === 'approved').length },
                  ],
                  historique: [
                    // 🗑 Fév 2026 — Sous-filtres retirés (Rejetés, Répertoire prix, Par produit, Archives)
                  ],
                  achats_manager: [
                    { key: 'achete', label: 'Produits achetés', count: expenses.filter(e => e.source === 'appro_manager' && e.payment_mode).length },
                  ],
                };
                const groupKey = ['en_cours', 'a_reviser', 'mes_demandes'].includes(achatsSubView) ? 'a_valider'
                  : ['valides'].includes(achatsSubView) ? 'valides_group'
                  : ['achete'].includes(achatsSubView) ? 'achats_manager'
                  : ['rejetes', 'repertoire_prix', 'par_produit', 'archives'].includes(achatsSubView) ? 'historique'
                  : 'a_valider';
                const list = subFilters[groupKey] || [];
                if (list.length <= 1) return null;
                return (
                  <div className="flex gap-1.5 flex-wrap items-center" data-testid="achats-subfilters">
                    {list.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setAchatsSubView(f.key)}
                        data-active={achatsSubView === f.key}
                        data-testid={`achats-subtab-${f.key.replace(/_/g, '-')}`}
                        className="ach-pill"
                      >
                        {f.label}
                        {typeof f.count === 'number' && f.count > 0 && (
                          <span className="ml-1.5 opacity-75 text-[9.5px] font-mono">{f.count}</span>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })()}

              {achatsSubView === 'repertoire_prix' ? <PurchasePriceHistoryTab /> :
               achatsSubView === 'par_produit' ? <ExpensesByProductTab /> :
               achatsSubView === 'archives' ? <ArchivedExpensesView currentUser={currentUser} /> : (<>

              {/* ═══ TOOLBAR compacte : tri + toggles icônes + filtres avancés ═══ */}
              <div className="flex flex-wrap gap-2 items-center">
                {/* Tri en select compact (sans label) */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-slate-900/70 border border-slate-700 text-slate-200 text-[11.5px] rounded-lg px-2.5 py-1.5"
                  data-testid="achats-sort-select"
                  title="Trier"
                >
                  <option value="date_desc">Plus récent</option>
                  <option value="date_asc">Plus ancien</option>
                  <option value="name_asc">A → Z</option>
                  <option value="name_desc">Z → A</option>
                  <option value="amount_desc">Montant ↓</option>
                  <option value="amount_asc">Montant ↑</option>
                </select>

                {/* Toggle Vue lignes/cartes */}
                <button
                  type="button"
                  onClick={() => setCompactMode(!compactMode)}
                  className="ach-icon-btn"
                  data-testid="toggle-compact-view"
                  title={compactMode ? "Passer en cartes détaillées" : "Passer en lignes compactes"}
                  style={compactMode ? { background: 'rgba(245, 158, 11, 0.15)', borderColor: 'rgba(245, 158, 11, 0.35)', color: '#fbbf24' } : {}}
                >
                  <LayoutList className="w-4 h-4" strokeWidth={2.4} />
                </button>

                {/* Toggle Voir tout en détail */}
                <button
                  type="button"
                  onClick={() => setShowAllExpenses(!showAllExpenses)}
                  className="ach-icon-btn"
                  title={showAllExpenses ? 'Masquer détails' : 'Voir tout en détail'}
                  style={showAllExpenses ? { background: 'rgba(6, 182, 212, 0.15)', borderColor: 'rgba(6, 182, 212, 0.35)', color: '#22d3ee' } : {}}
                >
                  <Eye className="w-4 h-4" strokeWidth={2.4} />
                </button>

                {/* Filtres avancés — visibles pour admin en_cours */}
                {currentUser?.role === 'admin' && achatsSubView === 'en_cours' && (
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters((s) => !s)}
                    className="ach-icon-btn"
                    title="Filtres avancés"
                    style={showAdvancedFilters || authorFilter !== 'all' ? { background: 'rgba(168, 85, 247, 0.15)', borderColor: 'rgba(168, 85, 247, 0.35)', color: '#c084fc' } : {}}
                    data-testid="achats-toggle-filters"
                  >
                    <SlidersHorizontal className="w-4 h-4" strokeWidth={2.4} />
                  </button>
                )}

                {/* Mode sélection multiple (admin) */}
                {currentUser?.role === 'admin' && !selectionMode && (
                  <button
                    type="button"
                    onClick={() => setSelectionMode(true)}
                    className="ach-icon-btn ml-auto"
                    title="Sélection multiple"
                    data-testid="enter-selection-mode-btn"
                  >
                    <CheckCircle className="w-4 h-4" strokeWidth={2.4} />
                  </button>
                )}
              </div>

              {/* ═══ FILTRE AUTEUR (avancé, replié par défaut) ═══ */}
              {currentUser?.role === 'admin' && achatsSubView === 'en_cours' && showAdvancedFilters && (
                <div className="p-3 rounded-xl bg-slate-900/50 border border-slate-800/70 space-y-2" data-testid="author-filter-bar">
                  <p className="text-slate-500 text-[10.5px] uppercase tracking-[0.13em] font-bold">Filtrer par auteur</p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[
                      { value: 'all',     label: 'Tous' },
                      { value: 'admin',   label: 'Admin' },
                      { value: 'manager', label: 'Resp. Op. & Log' },
                      { value: 'other',   label: 'Autres' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAuthorFilter(opt.value)}
                        data-active={authorFilter === opt.value}
                        data-testid={`author-filter-${opt.value}`}
                        className="ach-pill"
                      >
                        {opt.label}
                      </button>
                    ))}
                    {allAuthors.length > 0 && (
                      <select
                        value={['all','admin','manager','other'].includes(authorFilter) ? '' : authorFilter}
                        onChange={(e) => setAuthorFilter(e.target.value || 'all')}
                        className="ach-pill ml-1 !bg-slate-900/70 !text-slate-300"
                        data-testid="author-filter-named"
                        style={{ appearance: 'auto' }}
                      >
                        <option value="">— Auteur précis —</option>
                        {allAuthors.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              )}

              {/* === KPI Cards aérées (regroupement par état) === */}
              {expenses.length > 0 && (() => {
                const isAdmin = currentUser?.role === 'admin';
                // La Responsable Op. & Log ne voit pas les achats "terminés" (completed OU prestations payées) :
                // ils ne la concernent plus une fois la dépense réglée par l'admin / la prestation payée.
                const visibleExpenses = isAdmin ? expenses : expenses.filter(e => !isFinished(e));
                const sumBy = (statuses) => visibleExpenses
                  .filter(e => statuses.includes(e.status))
                  .reduce((s, e) => s + (e.amount || 0), 0);
                const cntBy = (statuses) => visibleExpenses.filter(e => statuses.includes(e.status)).length;
                const aTraiterAmount = sumBy(['pending', 'admin_review', 'revision_requested']);
                const aTraiterCount = cntBy(['pending', 'admin_review', 'revision_requested']);
                // Pour la Responsable Op. & Log : seulement "approved" non encore payées (prestations).
                // Pour l'admin : approved + completed.
                const validesStatuses = isAdmin ? ['approved', 'completed'] : ['approved'];
                const validesAmount = sumBy(validesStatuses);
                const validesCount = cntBy(validesStatuses);
                const totalAmount = visibleExpenses.reduce((s, e) => s + (e.amount || 0), 0);

                return (
                  <div className="grid grid-cols-3 gap-2" data-testid="achats-kpi-cards">
                    {/* À TRAITER */}
                    <button
                      type="button"
                      onClick={() => setAchatsSubView('en_cours')}
                      className="ach-kpi group focus:outline-none focus:ring-2 focus:ring-amber-500/60"
                      style={{ borderLeftColor: '#f59e0b' }}
                      data-testid="kpi-a-traiter-card"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="ach-kpi-label" style={{ color: '#fbbf24' }}>À traiter</p>
                        <AlertCircle className="w-3.5 h-3.5 text-amber-400/70 flex-shrink-0" strokeWidth={2.4} />
                      </div>
                      <p className="ach-kpi-value" data-testid="kpi-a-traiter-amount">
                        {formatPrice(aTraiterAmount)}<span className="text-[11px] text-amber-400/70 ml-0.5">F</span>
                      </p>
                      <p className="ach-kpi-sub">{aTraiterCount} demande{aTraiterCount > 1 ? 's' : ''}</p>
                    </button>

                    {/* VALIDÉS */}
                    <button
                      type="button"
                      onClick={() => setAchatsSubView('valides')}
                      className="ach-kpi group focus:outline-none focus:ring-2 focus:ring-green-500/60"
                      style={{ borderLeftColor: '#10b981' }}
                      data-testid="kpi-valides-card"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="ach-kpi-label" style={{ color: '#34d399' }}>{isAdmin ? "Terminés" : "Validés"}</p>
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400/70 flex-shrink-0" strokeWidth={2.4} />
                      </div>
                      <p className="ach-kpi-value" data-testid="kpi-valides-amount">
                        {formatPrice(validesAmount)}<span className="text-[11px] text-emerald-400/70 ml-0.5">F</span>
                      </p>
                      <p className="ach-kpi-sub">{validesCount} demande{validesCount > 1 ? 's' : ''}</p>
                    </button>

                    {/* TOTAL */}
                    <button
                      type="button"
                      onClick={() => setShowAllExpenses(true)}
                      className="ach-kpi group focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                      style={{ borderLeftColor: '#a855f7' }}
                      data-testid="kpi-total-card"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="ach-kpi-label" style={{ color: '#c084fc' }}>Total</p>
                        <Wallet className="w-3.5 h-3.5 text-purple-400/70 flex-shrink-0" strokeWidth={2.4} />
                      </div>
                      <p className="ach-kpi-value" data-testid="kpi-total-amount">
                        {formatPrice(totalAmount)}<span className="text-[11px] text-purple-400/70 ml-0.5">F</span>
                      </p>
                      {isAdmin && expenseRatioAlert ? (
                        <p
                          className={`ach-kpi-sub ${expenseRatioAlert.isOverLimit ? '!text-red-400' : '!text-emerald-400'}`}
                          data-testid="expense-ratio-ok"
                        >
                          Ratio {expenseRatioAlert.ratio}%
                        </p>
                      ) : (
                        <p className="ach-kpi-sub">{expenses.length} demande{expenses.length > 1 ? 's' : ''}</p>
                      )}
                    </button>
                  </div>
                );
              })()}

              {/* Barre bulk sticky en bas — visible uniquement si sélection active */}
              {currentUser?.role === 'admin' && selectionMode && (
                <div className="ach-sticky-bulk" data-testid="bulk-assign-bar">
                  <button
                    type="button"
                    onClick={() => { setSelectionMode(false); clearSelection(); }}
                    className="text-white/90 hover:text-white p-1"
                    title="Quitter le mode sélection"
                    data-testid="exit-selection-mode-btn"
                  >
                    <X className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-black text-[14px] leading-tight">
                      {selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}
                    </p>
                    <p className="text-white/80 text-[11px] leading-tight">
                      Mode sélection actif · cochez les demandes
                    </p>
                  </div>
                  {selectedIds.size > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setShowAssignDateDialog(true)}
                      className="bg-white text-purple-700 hover:bg-white/90 font-bold h-9 text-[12.5px]"
                      data-testid="open-assign-date-btn"
                    >
                      <Calendar className="w-3.5 h-3.5 mr-1" />
                      Rattacher…
                    </Button>
                  )}
                  {selectedIds.size > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearSelection}
                      className="text-white/90 hover:text-white h-9 text-[12px]"
                      data-testid="clear-selection-btn"
                    >
                      Vider
                    </Button>
                  )}
                </div>
              )}

              {/* FULL DETAIL VIEW - All expenses */}
              {showAllExpenses && expenses.length > 0 && (
                <Card className="bg-gradient-to-br from-indigo-900/20 to-purple-900/20 border-indigo-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-indigo-400 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        VUE COMPLÈTE - Toutes les demandes ({expenses.length})
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={printExpensesTicket}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                          title="Format ticket thermique 80mm"
                        >
                          <Receipt className="w-4 h-4 mr-1" />
                          Ticket 80mm
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={printAllExpensesList}
                          className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/20"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Imprimer A4
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-[500px] overflow-y-auto">
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                      <Edit2 className="w-3 h-3" />
                      Cliquez sur une ligne pour modifier la demande
                    </p>
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-800">
                        <tr className="text-left text-slate-400 border-b border-slate-700">
                          <th className="p-2">#</th>
                          <th className="p-2">Catégorie</th>
                          <th className="p-2">Libellé</th>
                          <th className="p-2 text-center">Qté</th>
                          <th className="p-2 text-right">P.U.</th>
                          <th className="p-2 text-right">Total</th>
                          <th className="p-2">Statut</th>
                          <th className="p-2">Semaine</th>
                          {currentUser?.role === 'admin' && <th className="p-2 text-center">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map((expense, index) => (
                          <>
                            <tr 
                              key={expense.id} 
                              className={`border-b border-slate-700/50 hover:bg-indigo-500/10 cursor-pointer transition-colors group ${expense.is_group ? 'bg-indigo-900/20' : ''}`}
                              onClick={() => openExpenseForEdit(expense)}
                              title="Cliquer pour modifier"
                            >
                              <td className="p-2 text-slate-500">{index + 1}</td>
                              <td className="p-2">
                                {expense.is_group ? (
                                  <Badge className="text-xs bg-indigo-500/30 text-indigo-300">
                                    📦 Liste
                                  </Badge>
                                ) : (
                                  <Badge className={`text-xs ${
                                    expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                    expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                    expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>{expense.category}</Badge>
                                )}
                              </td>
                              <td className="p-2 text-white flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (
                                  <span className="font-semibold">{expense.description} ({expense.items?.length || 0} articles)</span>
                                ) : expense.description}
                                <TypeDestBadges expense={expense} />
                                <Edit2 className="w-3 h-3 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </td>
                              <td className="p-2 text-center text-slate-300">{expense.is_group ? expense.items?.length : (expense.quantity || 1)}</td>
                              <td className="p-2 text-right text-slate-400">{expense.is_group ? '-' : formatPrice(expense.unit_price || expense.amount) + ' F'}</td>
                              <td className="p-2 text-right font-bold text-amber-400">{formatPrice(expense.amount)} F</td>
                              <td className="p-2">
                                <Badge className={`text-xs ${
                                  expense.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                  expense.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                  expense.status === 'completed' ? 'bg-slate-500/20 text-slate-400' :
                                  expense.status === 'revision_requested' ? 'bg-orange-500/20 text-orange-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {expense.status === 'pending' ? 'En attente' :
                                   expense.status === 'approved' ? 'Approuvée' :
                                   expense.status === 'completed' ? 'Terminée' :
                                   expense.status === 'revision_requested' ? 'À réviser' : 'Refusée'}
                                </Badge>
                              </td>
                              <td className="p-2">
                                {expense.assigned_week ? (
                                  <Badge className="text-xs bg-cyan-500/20 text-cyan-400">
                                    {format(new Date(expense.assigned_week), "dd/MM")}
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      setExpenseToAssign(expense);
                                      setShowWeekAssignModal(true);
                                    }}
                                    className="h-6 text-xs text-slate-500 hover:text-cyan-400 p-1"
                                  >
                                    <Calendar className="w-3 h-3 mr-1" />
                                    Rattacher
                                  </Button>
                                )}
                              </td>
                              {currentUser?.role === 'admin' && (
                                <td className="p-2 text-center">
                                  <Button 
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); deleteExpense(expense.id); }}
                                    className="h-7 w-7 p-0 text-red-500 hover:bg-red-700/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                            {/* Show sub-items for grouped lists */}
                            {expense.is_group && expense.items && sortItems(expense.items).map((item, subIndex) => (
                              <tr key={`${expense.id}-${subIndex}`} className="bg-slate-800/30 border-b border-slate-700/30 text-xs">
                                <td className="p-1 pl-6 text-slate-600">↳</td>
                                <td className="p-1">
                                  <Badge className={`text-xs ${
                                    item.category === 'cuisine' ? 'bg-green-500/10 text-green-500' :
                                    item.category === 'bar' ? 'bg-orange-500/10 text-orange-500' :
                                    item.category === 'paiement' ? 'bg-blue-500/10 text-blue-500' :
                                    'bg-slate-500/10 text-slate-500'
                                  }`}>{item.category}</Badge>
                                </td>
                                <td className="p-1 text-slate-400">{item.description}</td>
                                <td className="p-1 text-center text-slate-500">{item.quantity}</td>
                                <td className="p-1 text-right text-slate-500">{formatPrice(item.unit_price)} F</td>
                                <td className="p-1 text-right text-slate-400">{formatPrice(item.amount)} F</td>
                                <td className="p-1"></td>
                                <td className="p-1"></td>
                                {currentUser?.role === 'admin' && <td className="p-1"></td>}
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-800 font-bold">
                          <td colSpan="5" className="p-2 text-right text-slate-400">TOTAL GÉNÉRAL:</td>
                          <td className="p-2 text-right text-lg text-indigo-400">{formatPrice(expenses.reduce((sum, e) => sum + e.amount, 0))} F</td>
                          <td colSpan={currentUser?.role === 'admin' ? 3 : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* === MODE LIGNES COMPACTES (par défaut) === */}
              {isCompactView && (
                <ExpenseCompactList
                  expenses={expenses}
                  currentUser={currentUser}
                  achatsSubView={achatsSubView}
                  matchesAuthorFilter={matchesAuthorFilter}
                  isFinished={isFinished}
                  sortExpenses={sortExpenses}
                  updateExpense={updateExpense}
                  deleteExpense={deleteExpense}
                  openExpenseForEdit={openExpenseForEdit}
                  openReviseModal={openReviseModal}
                  convertExpenseToPO={convertExpenseToPO}
                  printSingleExpenseTicket={printSingleExpenseTicket}
                  printExpensePDF={printExpensePDF}
                  allocateExpenseToAccount={allocateExpenseToAccount}
                  availableAccounts={availableAccounts}
                  receiveExpenseStock={receiveExpenseStock}
                  fetchExpenses={fetchExpenses}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  toggleSelection={toggleSelection}
                />
              )}

              {/* === Cards détaillées (Vue : cartes) === */}
              {!isCompactView && (<>

              {/* Pending expenses that need manager revision (revision_requested) */}
              {achatsSubView === 'a_reviser' && currentUser?.role === 'manager' && expenses.filter(e => e.status === 'revision_requested').length > 0 && (
                <Card className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border-amber-500/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-amber-400 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      À RÉVISER
                      <Badge className="bg-amber-500/30 text-amber-300 ml-2">
                        {expenses.filter(e => e.status === 'revision_requested').length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {sortExpenses(expenses.filter(e => e.status === 'revision_requested')).map(expense => (
                      <div key={expense.id} className="bg-amber-900/20 rounded-lg p-3 border border-amber-500/30">
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (
                                  <Badge className="text-xs bg-indigo-500/30 text-indigo-300">📦 Liste</Badge>
                                ) : (
                                  <Badge className={`text-xs ${
                                    expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                    expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                    expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>{expense.category}</Badge>
                                )}
                                <span className="text-white font-medium">{expense.description}</span>
                              </div>
                              {!expense.is_group && (
                                <div className="text-slate-400 text-sm mt-1">
                                  Qté: <span className="text-white">{expense.quantity || 1}</span> × 
                                  PU: <span className="text-white">{formatPrice(expense.unit_price || expense.amount)} F</span>
                                </div>
                              )}
                              <p className="text-amber-400 font-bold text-lg">{formatPrice(expense.amount)} F</p>
                              {expense.admin_notes && (
                                <p className="text-amber-300 text-sm mt-1">
                                  <strong>Note admin:</strong> {expense.admin_notes}
                                </p>
                              )}
                            </div>
                            <Button 
                              size="sm"
                              onClick={() => openExpenseForEdit(expense)}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                          </div>
                          {/* Show sub-items for grouped lists */}
                          {expense.is_group && expense.items && expense.items.length > 0 && (
                            <div className="bg-slate-800/50 rounded p-2 mt-1">
                              <p className="text-xs text-slate-400 mb-2">Détails de la liste ({expense.items.length} articles):</p>
                              <div className="space-y-1">
                                {sortItems(expense.items).map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-700/50 pb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-500">{idx + 1}.</span>
                                      <Badge className={`text-xs ${
                                        item.category === 'cuisine' ? 'bg-green-500/10 text-green-500' :
                                        item.category === 'bar' ? 'bg-orange-500/10 text-orange-500' :
                                        item.category === 'paiement' ? 'bg-blue-500/10 text-blue-500' :
                                        'bg-slate-500/10 text-slate-500'
                                      }`}>{item.category}</Badge>
                                      <span className="text-white">{item.description}</span>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-slate-400 text-xs">{item.quantity} × {formatPrice(item.unit_price)} = </span>
                                      <span className="text-amber-400 font-bold">{formatPrice(item.amount)} F</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Admin: Purchases sent for manager revision — still visible to admin with re-approve/re-revise controls */}
              {achatsSubView === 'a_reviser' && currentUser?.role === 'admin' && expenses.filter(e => e.status === 'revision_requested').length > 0 && (
                <Card className="bg-gradient-to-br from-orange-900/30 to-amber-900/20 border-orange-500/50" data-testid="admin-revision-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-orange-400 flex items-center gap-2">
                      <Edit2 className="w-5 h-5" />
                      MODIFIÉS — EN COURS DE RÉVISION CHEZ LA RESPONSABLE OP. & LOG
                      <Badge className="bg-orange-500/30 text-orange-300 ml-2">
                        {expenses.filter(e => e.status === 'revision_requested').length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-slate-400">
                      Ces demandes ont été modifiées par vous et renvoyées à la responsable op. & log. Vous pouvez les approuver directement ou demander une nouvelle révision.
                    </p>
                    {sortExpenses(expenses.filter(e => e.status === 'revision_requested')).map(expense => (
                      <div key={expense.id} className="bg-orange-900/20 rounded-lg p-3 border border-orange-500/30">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (
                                  <Badge className="text-xs bg-indigo-500/30 text-indigo-300">📦 Liste ({expense.items?.length || 0} articles)</Badge>
                                ) : (
                                  <Badge className={`text-xs ${
                                    expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                    expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                    expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>{expense.category}</Badge>
                                )}
                                <span className="text-white font-medium">{expense.description}</span>
                              </div>
                              <p className="text-orange-300 font-bold text-lg mt-1">{formatPrice(expense.amount)} F</p>
                              {expense.supplier && <p className="text-slate-500 text-xs">Fournisseur: {expense.supplier}</p>}
                              <p className="text-slate-500 text-xs">
                                Demandé par: {expense.requested_by} • Modifié le {expense.updated_at ? new Date(expense.updated_at).toLocaleDateString('fr-FR') : '-'}
                              </p>
                              {expense.admin_notes && (
                                <p className="text-orange-200 text-sm mt-1 italic">
                                  <strong>Note:</strong> {expense.admin_notes}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-wrap shrink-0">
                              <Button
                                size="sm"
                                onClick={() => updateExpense(expense.id, { status: "approved", approved_by: "Administrateur" })}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`admin-revision-approve-${expense.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReviseModal(expense)}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                data-testid={`admin-revision-revise-${expense.id}`}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Nouvelle révision
                              </Button>
                            </div>
                          </div>
                          {/* Sub-items for grouped lists */}
                          {expense.is_group && expense.items && expense.items.length > 0 && (
                            <div className="bg-slate-800/50 rounded p-2 mt-1">
                              <p className="text-xs text-slate-400 mb-2">📋 Détails de la liste:</p>
                              <div className="space-y-1">
                                {sortItems(expense.items).map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-xs border-b border-slate-700/50 pb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-500">{idx + 1}.</span>
                                      <span className="text-white">{item.description}</span>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-slate-400">{item.quantity} × {formatPrice(item.unit_price)} = </span>
                                      <span className="text-orange-300 font-bold">{formatPrice(item.amount)} F</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Pending validations (admin: full controls, manager: read-only) */}
              {achatsSubView === 'en_cours' && expenses.filter(e => e.status === 'pending' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e))).length > 0 && (
                <Card className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border-purple-500/50" data-testid="pending-expenses-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-purple-400 flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      {currentUser?.role === 'admin' ? 'DEMANDES À VALIDER' : 'EN ATTENTE DE VALIDATION'}
                      <Badge className="bg-purple-500/30 text-purple-300 ml-2">
                        {expenses.filter(e => e.status === 'pending' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e))).length}
                      </Badge>
                      {currentUser?.role !== 'admin' && (
                        <Badge className="bg-slate-500/30 text-slate-300 ml-auto text-xs">
                          Lecture seule
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {sortExpenses(expenses.filter(e => e.status === 'pending' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e)))).map(expense => (
                      <div key={expense.id} className="bg-purple-900/20 rounded-lg p-4 border border-purple-500/30">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (
                                  <Badge className="text-xs bg-indigo-500/30 text-indigo-300">📦 Liste ({expense.items?.length || 0} articles)</Badge>
                                ) : (
                                  <Badge className={`text-xs ${
                                    expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                    expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                    expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>{expense.category}</Badge>
                                )}
                                <span className="text-white font-bold">{expense.description}</span>
                              </div>
                              {/* Show quantity and unit price for single items */}
                              {!expense.is_group && (
                                <div className="text-slate-300 text-sm mt-1 bg-slate-800/50 rounded px-2 py-1 inline-block">
                                  <span className="text-slate-400">Qté:</span> <span className="font-bold">{expense.quantity || 1}</span>
                                  <span className="mx-2">×</span>
                                  <span className="text-slate-400">PU:</span> <span className="font-bold">{formatPrice(expense.unit_price || expense.amount)} F</span>
                                  <span className="mx-2">=</span>
                                  <span className="text-amber-400 font-bold">{formatPrice(expense.amount)} F</span>
                                </div>
                              )}
                              {expense.is_group && (
                                <p className="text-amber-400 font-bold text-lg mt-1">Total: {formatPrice(expense.amount)} F</p>
                              )}
                              <p className="text-slate-400 text-sm mt-1">
                                Demandé par: {expense.requested_by} • {new Date(expense.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              {expense.supplier && <p className="text-slate-500 text-sm">Fournisseur: {expense.supplier}</p>}
                              {expense.planned_date && <p className="text-slate-500 text-sm">Prévu le: {expense.planned_date}</p>}
                              {/* Badges d'analyse admin (doublons, stock, trésorerie) */}
                              {currentUser?.role === 'admin' && expenseAnalyses[expense.id] && (
                                <div className="mt-2">
                                  <ExpenseAnalysisBadges analysis={expenseAnalyses[expense.id]} />
                                </div>
                              )}
                              {expense.receipt_image && (
                                <div className="mt-2">
                                  <img 
                                    src={expense.receipt_image} 
                                    alt="Reçu" 
                                    className="max-w-[200px] max-h-[100px] object-cover rounded border border-slate-600 cursor-pointer"
                                    onClick={() => window.open(expense.receipt_image, '_blank')}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Show sub-items for grouped lists */}
                          {expense.is_group && expense.items && expense.items.length > 0 && (() => {
                            const isAdmin = currentUser?.role === 'admin';
                            const editedItems = isAdmin ? getEditedItems(expense) : expense.items;
                            const keptTotal = editedItems.reduce(
                              (s, it) => s + (it.struck ? 0 : (it.amount || 0)), 0
                            );
                            const struckCount = editedItems.filter((it) => it.struck).length;
                            return (
                            <div className="bg-slate-800/50 rounded p-3 border border-slate-700" data-testid={`pending-items-${expense.id}`}>
                              <p className="text-xs text-slate-400 mb-2 font-semibold flex items-center gap-2">
                                📋 Détails de la liste
                                {isAdmin && (
                                  <span className="text-[10px] text-slate-500 font-normal italic">
                                    — cochez les lignes à rayer puis approuvez
                                  </span>
                                )}
                              </p>
                              <div className="space-y-2">
                                {editedItems.map((item, idx) => {
                                  const struck = !!item.struck;
                                  return (
                                  <div
                                    key={idx}
                                    className={`flex flex-col gap-2 text-sm rounded p-2 transition-colors ${
                                      struck
                                        ? 'bg-red-900/20 border border-red-500/30 opacity-60'
                                        : 'bg-slate-900/30'
                                    }`}
                                    data-testid={`pending-item-${expense.id}-${idx}`}
                                  >
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {isAdmin && (
                                          <Checkbox
                                            checked={struck}
                                            onCheckedChange={(checked) => updateStrikeItem(expense.id, idx, {
                                              struck: !!checked,
                                              strike_reason: checked ? (item.strike_reason || 'pas_opportun') : '',
                                              strike_note: checked ? item.strike_note : '',
                                            })}
                                            className="border-red-400 data-[state=checked]:bg-red-600 shrink-0"
                                            data-testid={`strike-checkbox-${expense.id}-${idx}`}
                                          />
                                        )}
                                        <span className={`text-slate-500 font-mono shrink-0 ${struck ? 'line-through' : ''}`}>{idx + 1}.</span>
                                        <Badge className={`text-xs shrink-0 ${
                                          item.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                          item.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                          item.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                          'bg-slate-500/20 text-slate-400'
                                        }`}>{item.category}</Badge>
                                        <span className={`text-white font-medium truncate ${struck ? 'line-through text-slate-400' : ''}`}>{item.description}</span>
                                      </div>
                                      <div className="text-right flex items-center gap-2 shrink-0 pl-7 sm:pl-0">
                                        <span className={`text-slate-400 text-xs ${struck ? 'line-through' : ''}`}>
                                          {item.quantity} × {formatPrice(item.unit_price)} F
                                        </span>
                                        <span className={`font-bold ${struck ? 'line-through text-slate-500' : 'text-amber-400'}`}>{formatPrice(item.amount)} F</span>
                                      </div>
                                    </div>
                                    {/* Strike reason controls (admin + struck) */}
                                    {isAdmin && struck && (
                                      <div className="flex items-center gap-2 pl-7 flex-wrap">
                                        <Ban className="w-3.5 h-3.5 text-red-400 shrink-0" />
                                        <span className="text-[11px] text-red-300">Motif :</span>
                                        <select
                                          value={item.strike_reason || 'pas_opportun'}
                                          onChange={(e) => updateStrikeItem(expense.id, idx, { strike_reason: e.target.value })}
                                          className="bg-slate-800 border border-red-500/40 rounded px-2 py-1 text-xs text-red-200"
                                          data-testid={`strike-reason-${expense.id}-${idx}`}
                                        >
                                          {STRIKE_REASONS.map((r) => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                          ))}
                                        </select>
                                        {item.strike_reason === 'autres' && (
                                          <Input
                                            placeholder="Préciser…"
                                            value={item.strike_note || ''}
                                            onChange={(e) => updateStrikeItem(expense.id, idx, { strike_note: e.target.value })}
                                            className="h-7 flex-1 min-w-[140px] bg-slate-800 border-red-500/40 text-xs text-red-200"
                                            data-testid={`strike-note-${expense.id}-${idx}`}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                              <div className="border-t border-slate-700 mt-2 pt-2 flex justify-end items-center gap-3">
                                {isAdmin && struckCount > 0 && (
                                  <span className="text-[11px] text-red-300">
                                    {struckCount} ligne{struckCount > 1 ? 's' : ''} rayée{struckCount > 1 ? 's' : ''} — total recalculé
                                  </span>
                                )}
                                <span className="text-slate-400">Total {isAdmin && struckCount > 0 ? 'à approuver' : 'liste'} :</span>
                                <span className="text-amber-400 font-bold ml-2" data-testid={`pending-kept-total-${expense.id}`}>{formatPrice(keptTotal)} F</span>
                              </div>
                            </div>
                            );
                          })()}
                          {/* Admin: Montant modifiable directement */}
                          {currentUser?.role === 'admin' && (
                          <>
                          <div className="flex items-center gap-3 flex-wrap bg-slate-800/50 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <Label className="text-slate-400 text-sm">Montant total:</Label>
                              <Input
                                type="number"
                                defaultValue={expense.amount}
                                className="w-32 bg-slate-700/50 border-slate-600 text-white text-lg font-bold"
                                id={`admin-amount-${expense.id}`}
                              />
                              <span className="text-slate-400">F</span>
                            </div>
                            <Input
                              placeholder="Note pour la responsable op. & log (optionnel)"
                              className="flex-1 min-w-[200px] bg-slate-700/50 border-slate-600 text-white text-sm"
                              id={`admin-note-${expense.id}`}
                            />
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm"
                              onClick={() => handleAdminFirstValidation(expense)}
                              className="bg-amber-600 hover:bg-amber-700"
                              data-testid={`first-validate-btn-${expense.id}`}
                              title="Sauvegarde dans votre profil — vous pourrez encore modifier avant l'envoi à la responsable op. & log"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Première validation
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleApproveDirectly(expense)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`direct-approve-btn-${expense.id}`}
                              title="Valider directement sans modification — la liste est envoyée à la responsable op. & log telle quelle"
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Valider sans modifier
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => updateExpense(expense.id, { status: "rejected" })}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              data-testid={`reject-btn-${expense.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Refuser
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => deleteExpense(expense.id)}
                              className="border-red-700/50 text-red-500 hover:bg-red-700/20"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                          </>
                          )}
                          {/* Manager: read-only banner */}
                          {currentUser?.role !== 'admin' && (
                            <div className="bg-slate-800/50 rounded-lg p-3 text-slate-400 text-sm flex items-center gap-2">
                              <ShoppingCart className="w-4 h-4 text-purple-400" />
                              Demande transmise à l'administrateur — en attente de validation.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* ============================================================
                   ADMIN REVIEW SECTION (status='admin_review')
                   - Admin: full inline editing of items, PDF preview, send to manager
                   - Manager: read-only with original list snapshot + locked banner
                  ============================================================ */}
              {achatsSubView === 'en_cours' && expenses.filter(e => e.status === 'admin_review' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e))).length > 0 && (
                <Card className={`bg-gradient-to-br ${currentUser?.role === 'admin' ? 'from-amber-900/30 to-orange-900/20 border-amber-500/50' : 'from-slate-900/40 to-slate-800/20 border-slate-600/50'}`} data-testid="admin-review-expenses-card">
                  <CardHeader className="pb-2">
                    <CardTitle className={`flex items-center gap-2 ${currentUser?.role === 'admin' ? 'text-amber-300' : 'text-slate-300'}`}>
                      <Edit2 className="w-5 h-5" />
                      {currentUser?.role === 'admin'
                        ? 'EN COURS DE CORRECTION (votre profil)'
                        : 'EN COURS DE VALIDATION PAR L\'ADMIN'}
                      <Badge className={`ml-2 ${currentUser?.role === 'admin' ? 'bg-amber-500/30 text-amber-200' : 'bg-slate-500/30 text-slate-300'}`}>
                        {expenses.filter(e => e.status === 'admin_review' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e))).length}
                      </Badge>
                      {currentUser?.role !== 'admin' && (
                        <Badge className="ml-auto bg-slate-700/40 text-slate-300 text-xs flex items-center gap-1">
                          🔒 Lecture seule
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {sortExpenses(expenses.filter(e => e.status === 'admin_review' && (currentUser?.role !== 'admin' || matchesAuthorFilter(e)))).map(expense => {
                      const isAdmin = currentUser?.role === 'admin';
                      // Manager sees the ORIGINAL snapshot (before admin started correcting)
                      const managerItems = expense.original_items || expense.items || [];
                      const managerAmount = expense.original_amount || expense.amount;
                      const correctedItems = isAdmin ? getEditedItems(expense) : (expense.items || []);
                      const correctedAmount = correctedItems.reduce((s, it) => s + (it.struck ? 0 : (it.amount || 0)), 0);
                      // Default view: admin sees their corrections, responsable op. & log sees the original
                      const viewMode = getReviewViewMode(expense.id, isAdmin ? 'corrected' : 'original');
                      const showOriginal = viewMode === 'original';
                      const hasOriginalSnapshot = !!expense.original_items;
                      const editedItems = isAdmin ? correctedItems : managerItems;
                      const keptTotal = showOriginal ? managerAmount : correctedAmount;
                      const struckCount = isAdmin ? correctedItems.filter(it => it.struck).length : 0;
                      return (
                        <div
                          key={expense.id}
                          className={`rounded-lg p-4 border relative ${selectionMode && selectedIds.has(expense.id) ? 'border-purple-500/70 ring-1 ring-purple-500/40' : (isAdmin ? 'bg-amber-900/15 border-amber-500/30' : 'bg-slate-800/40 border-slate-600/40')} ${selectionMode && !selectedIds.has(expense.id) ? (isAdmin ? 'bg-amber-900/15' : 'bg-slate-800/40') : ''}`}
                          onClick={selectionMode ? () => toggleSelection(expense.id) : undefined}
                          style={selectionMode ? { cursor: 'pointer' } : undefined}
                        >
                          {selectionMode && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(expense.id)}
                              onChange={() => toggleSelection(expense.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute top-2 left-2 w-4 h-4 accent-purple-500 cursor-pointer z-10"
                              data-testid={`select-expense-${expense.id}`}
                            />
                          )}
                          {expense.assigned_date && (
                            <span className="absolute top-2 right-2 text-[10px] bg-purple-500/30 text-purple-200 border border-purple-500/50 rounded-full px-2 py-0.5 z-10" data-testid={`assigned-badge-${expense.id}`}>
                              📌 {expense.assignment_precision === 'month' ? `Rattaché à ${expense.assigned_date.slice(0,7)}` : `Rattaché au ${expense.assigned_date}`}
                            </span>
                          )}
                          {/* Header */}
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (
                                  <Badge className="text-xs bg-indigo-500/30 text-indigo-300">
                                    📦 Liste ({(isAdmin ? editedItems : managerItems).length} articles)
                                  </Badge>
                                ) : (
                                  <Badge className="text-xs bg-slate-500/20 text-slate-400">{expense.category}</Badge>
                                )}
                                <span className="text-white font-bold">{expense.description}</span>
                                {!isAdmin && (
                                  <Badge className="text-xs bg-slate-700/40 text-slate-300 border border-slate-500/40 flex items-center gap-1">
                                    🔒 En cours de validation par l'admin
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">
                                Demandé par : {expense.requested_by} • {new Date(expense.created_at).toLocaleDateString('fr-FR')}
                                {expense.admin_review_at && (
                                  <> • <span className="text-amber-300">Validation initiale : {new Date(expense.admin_review_at).toLocaleString('fr-FR')}</span></>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* === Toggle: Liste d'origine / Liste corrigée === */}
                          {expense.is_group && hasOriginalSnapshot && (
                            <div className="mt-3 flex items-center gap-1 bg-slate-900/40 border border-slate-700 rounded p-1 w-fit" data-testid={`review-view-toggle-${expense.id}`}>
                              <button
                                type="button"
                                onClick={() => setReviewViewModeFor(expense.id, 'original')}
                                className={`px-3 py-1 text-xs rounded transition-colors ${
                                  showOriginal
                                    ? 'bg-slate-600 text-white font-semibold'
                                    : 'text-slate-400 hover:text-slate-200'
                                }`}
                                data-testid={`review-view-original-${expense.id}`}
                              >
                                📋 Liste d'origine
                              </button>
                              <button
                                type="button"
                                onClick={() => setReviewViewModeFor(expense.id, 'corrected')}
                                className={`px-3 py-1 text-xs rounded transition-colors ${
                                  !showOriginal
                                    ? 'bg-amber-600 text-white font-semibold'
                                    : 'text-slate-400 hover:text-slate-200'
                                }`}
                                data-testid={`review-view-corrected-${expense.id}`}
                              >
                                ✏️ Liste corrigée
                              </button>
                            </div>
                          )}

                          {/* === ADMIN: full inline editor (only when 'corrected' view) === */}
                          {isAdmin && expense.is_group && !showOriginal && (
                            <div className="mt-3 bg-slate-900/40 rounded p-3 border border-amber-500/20" data-testid={`admin-review-editor-${expense.id}`}>
                              <p className="text-xs text-amber-200 mb-2 font-semibold">
                                ✏️ Édition libre — vous pouvez modifier les quantités, prix, ajouter/retirer des lignes ou raturer.
                              </p>
                              <div className="space-y-2">
                                {editedItems.map((item, idx) => {
                                  const struck = !!item.struck;
                                  return (
                                    <div
                                      key={idx}
                                      className={`flex flex-col gap-2 rounded p-2 transition-colors ${
                                        struck ? 'bg-red-900/20 border border-red-500/30 opacity-70' : 'bg-slate-800/50 border border-slate-700'
                                      }`}
                                      data-testid={`admin-review-item-${expense.id}-${idx}`}
                                    >
                                      {/* MOBILE-FRIENDLY: row 1 = top controls; row 2 = numeric controls */}
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Checkbox
                                          checked={struck}
                                          onCheckedChange={(checked) => updateAdminReviewItem(expense, idx, {
                                            struck: !!checked,
                                            strike_reason: checked ? (item.strike_reason || 'pas_opportun') : '',
                                            strike_note: checked ? item.strike_note : '',
                                          })}
                                          className="border-red-400 data-[state=checked]:bg-red-600 shrink-0"
                                          data-testid={`admin-review-strike-${expense.id}-${idx}`}
                                        />
                                        <span className={`text-slate-500 font-mono text-xs shrink-0 ${struck ? 'line-through' : ''}`}>{idx + 1}.</span>
                                        <Input
                                          value={item.description}
                                          onChange={(e) => updateAdminReviewItem(expense, idx, { description: e.target.value })}
                                          placeholder="Description"
                                          className={`flex-1 min-w-[120px] h-8 bg-slate-900 border-slate-600 text-white text-sm ${struck ? 'line-through' : ''}`}
                                          data-testid={`admin-review-desc-${expense.id}-${idx}`}
                                        />
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => removeAdminReviewItem(expense, idx)}
                                          className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/20 shrink-0"
                                          title="Supprimer la ligne"
                                          data-testid={`admin-review-remove-${expense.id}-${idx}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                      <div className="flex items-center gap-2 flex-wrap pl-7">
                                        <select
                                          value={item.category}
                                          onChange={(e) => updateAdminReviewItem(expense, idx, { category: e.target.value })}
                                          className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white shrink-0"
                                        >
                                          <option value="cuisine">🍳 Cuisine</option>
                                          <option value="bar">🍹 Bar</option>
                                          <option value="paiement">💳 Paiement</option>
                                          <option value="autres">📦 Autres</option>
                                        </select>
                                        <div className="flex items-center gap-1">
                                          <span className="text-slate-500 text-[10px]">Qté</span>
                                          <Input
                                            type="number"
                                            step="any"
                                            value={item.quantity}
                                            onChange={(e) => updateAdminReviewItem(expense, idx, { quantity: e.target.value })}
                                            placeholder="0"
                                            className="w-16 h-8 bg-slate-900 border-slate-600 text-white text-sm text-right"
                                            data-testid={`admin-review-qty-${expense.id}-${idx}`}
                                          />
                                        </div>
                                        <span className="text-slate-500 text-xs">×</span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-slate-500 text-[10px]">PU</span>
                                          <Input
                                            type="number"
                                            step="any"
                                            value={item.unit_price}
                                            onChange={(e) => updateAdminReviewItem(expense, idx, { unit_price: e.target.value })}
                                            placeholder="0"
                                            className="w-24 h-8 bg-slate-900 border-slate-600 text-white text-sm text-right"
                                            data-testid={`admin-review-pu-${expense.id}-${idx}`}
                                          />
                                        </div>
                                        <span className="text-slate-500 text-xs">=</span>
                                        <span className={`font-bold text-sm ml-auto ${struck ? 'line-through text-slate-500' : 'text-amber-400'}`}>
                                          {formatPrice((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))} F
                                        </span>
                                      </div>
                                      {struck && (
                                        <div className="flex items-center gap-2 pl-7 flex-wrap">
                                          <Ban className="w-3.5 h-3.5 text-red-400 shrink-0" />
                                          <span className="text-[11px] text-red-300">Motif :</span>
                                          <select
                                            value={item.strike_reason || 'pas_opportun'}
                                            onChange={(e) => updateAdminReviewItem(expense, idx, { strike_reason: e.target.value })}
                                            className="bg-slate-900 border border-red-500/40 rounded px-2 py-1 text-xs text-red-200"
                                          >
                                            {STRIKE_REASONS.map((r) => (
                                              <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                          </select>
                                          {item.strike_reason === 'autres' && (
                                            <Input
                                              placeholder="Préciser…"
                                              value={item.strike_note || ''}
                                              onChange={(e) => updateAdminReviewItem(expense, idx, { strike_note: e.target.value })}
                                              className="h-7 flex-1 min-w-[140px] bg-slate-900 border-red-500/40 text-xs text-red-200"
                                            />
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-amber-500/20 pt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => addAdminReviewItem(expense)}
                                  className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 h-8"
                                  data-testid={`admin-review-add-${expense.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Ajouter une ligne
                                </Button>
                                <div className="flex items-center gap-3">
                                  {struckCount > 0 && (
                                    <span className="text-[11px] text-red-300">
                                      {struckCount} ligne{struckCount > 1 ? 's' : ''} rayée{struckCount > 1 ? 's' : ''}
                                    </span>
                                  )}
                                  <span className="text-slate-300 text-sm">Total provisoire :</span>
                                  <span className="text-amber-300 font-bold text-lg" data-testid={`admin-review-total-${expense.id}`}>
                                    {formatPrice(keptTotal)} F
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* === READ-ONLY VIEW: shown to manager always, and to admin when 'original' is selected === */}
                          {expense.is_group && (!isAdmin || showOriginal) && (
                            <div className={`mt-3 rounded p-3 border ${showOriginal ? 'bg-slate-900/40 border-slate-600/40' : 'bg-amber-900/10 border-amber-500/30'}`}
                                 data-testid={`review-readonly-list-${expense.id}`}>
                              <p className={`text-xs mb-2 italic ${showOriginal ? 'text-slate-400' : 'text-amber-200'}`}>
                                {showOriginal
                                  ? "Liste d'origine (telle que soumise par la responsable op. & log)"
                                  : "Liste corrigée par l'admin (en cours de validation)"}
                                  :
                              </p>
                              <div className="space-y-1">
                                {(showOriginal ? managerItems : (expense.items || [])).map((item, idx) => {
                                  const struck = !!item.struck;
                                  return (
                                    <div key={idx} className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 text-sm rounded p-2 ${
                                      struck ? 'bg-red-900/15 border border-red-500/20 opacity-70' : 'bg-slate-800/40'
                                    }`}>
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className={`text-slate-500 font-mono text-xs shrink-0 ${struck ? 'line-through' : ''}`}>{idx + 1}.</span>
                                        <Badge className={`text-[10px] shrink-0 ${
                                          item.category === 'cuisine' ? 'bg-green-500/10 text-green-400' :
                                          item.category === 'bar' ? 'bg-orange-500/10 text-orange-400' :
                                          item.category === 'paiement' ? 'bg-blue-500/10 text-blue-400' :
                                          'bg-slate-500/10 text-slate-400'
                                        }`}>{item.category}</Badge>
                                        <span className={`text-slate-200 truncate ${struck ? 'line-through text-slate-400' : ''}`}>{item.description}</span>
                                      </div>
                                      <div className="text-right text-xs shrink-0 pl-7 sm:pl-0">
                                        <span className={`text-slate-400 ${struck ? 'line-through' : ''}`}>{item.quantity} × {formatPrice(item.unit_price)} = </span>
                                        <span className={`font-bold ${struck ? 'line-through text-slate-500' : 'text-slate-200'}`}>{formatPrice(item.amount)} F</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-2 pt-2 border-t border-slate-700 flex flex-wrap justify-end items-center gap-2">
                                <span className="text-slate-400 text-sm">
                                  {showOriginal ? "Total d'origine :" : "Total corrigé :"}
                                </span>
                                <span className={`font-bold ${showOriginal ? 'text-slate-200' : 'text-amber-300'}`}>
                                  {formatPrice(showOriginal ? managerAmount : correctedAmount)} F
                                </span>
                              </div>
                            </div>
                          )}

                          {/* === ACTIONS === */}
                          {isAdmin && (
                            <div className="mt-3 flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() => handleSaveAdminReviewEdits(expense)}
                                className="bg-slate-600 hover:bg-slate-700"
                                data-testid={`admin-review-save-${expense.id}`}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Enregistrer modifications
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Build a temp expense reflecting current edits for the PDF preview.
                                  const editedSnapshot = {
                                    ...expense,
                                    items: getEditedItems(expense).map((it) => ({
                                      ...it,
                                      amount: (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
                                    })),
                                  };
                                  printExpensePDF(editedSnapshot);
                                }}
                                className="border-blue-500/50 text-blue-300 hover:bg-blue-500/20"
                                data-testid={`admin-review-preview-${expense.id}`}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Aperçu PDF
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSendToManager(expense)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`admin-review-send-${expense.id}`}
                              >
                                <Truck className="w-4 h-4 mr-1" />
                                Envoyer à la responsable op. & log
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateExpense(expense.id, { status: "pending" })}
                                className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                                data-testid={`admin-review-back-${expense.id}`}
                                title="Revenir à l'étape précédente (annule la première validation)"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Annuler validation
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}


              {/* Approved expenses (ready for purchase) */}
              {achatsSubView === 'valides' && (() => {
                // La Responsable Op. & Log ne voit plus les prestations déjà payées (elles vont dans Terminés admin).
                const approvedVisible = sortExpenses(expenses.filter(e =>
                  e.status === 'approved' && (isAdminUser || !(e.category === 'paiement' && e.is_paid === true))
                ));
                if (approvedVisible.length === 0) return null;
                return (
                <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/20 border-green-500/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-green-400 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        APPROUVÉS - Prêts à acheter
                        <Badge className="bg-green-500/30 text-green-300 ml-2">
                          {approvedVisible.length}
                        </Badge>
                        <Badge className="bg-emerald-500/30 text-emerald-300">
                          Total: {formatPrice(approvedVisible.reduce((sum, e) => sum + e.amount, 0))} F
                        </Badge>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={printExpensesTicket}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                        >
                          <Receipt className="w-4 h-4 mr-1" />
                          Ticket 80mm
                        </Button>
                        <Button 
                          size="sm"
                          onClick={printAllApprovedExpenses}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Imprimer A4
                        </Button>
                        <Button
                          size="sm"
                          onClick={printApprovedExpensesDetailed}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid="print-approved-detailed-btn"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Détail par achat
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {approvedVisible.map(expense => (
                      <div
                        key={expense.id}
                        className={`bg-green-900/20 rounded-lg p-3 border ${selectionMode && selectedIds.has(expense.id) ? 'border-purple-500/70 ring-1 ring-purple-500/40' : 'border-green-500/30'} relative`}
                        onClick={selectionMode ? () => toggleSelection(expense.id) : undefined}
                        style={selectionMode ? { cursor: 'pointer' } : undefined}
                      >
                        {selectionMode && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(expense.id)}
                            onChange={() => toggleSelection(expense.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-2 left-2 w-4 h-4 accent-purple-500 cursor-pointer z-10"
                            data-testid={`select-expense-${expense.id}`}
                          />
                        )}
                        {expense.assigned_date && (
                          <span className="absolute top-2 right-2 text-[10px] bg-purple-500/30 text-purple-200 border border-purple-500/50 rounded-full px-2 py-0.5 z-10" data-testid={`assigned-badge-${expense.id}`}>
                            📌 {expense.assignment_precision === 'month' ? `Rattaché à ${expense.assigned_date.slice(0,7)}` : `Rattaché au ${expense.assigned_date}`}
                          </span>
                        )}
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {expense.is_group ? (() => {
                                  const visible = (expense.items || []).filter(it => !it.struck).length;
                                  const struck = (expense.items || []).filter(it => it.struck).length;
                                  return (
                                    <>
                                      <Badge className="text-xs bg-indigo-500/30 text-indigo-300">📦 Liste ({visible} articles)</Badge>
                                      {struck > 0 && currentUser?.role === 'admin' && (
                                        <Badge className="text-xs bg-red-500/20 text-red-300 border border-red-500/40" data-testid={`struck-count-${expense.id}`}>
                                          🚫 {struck} ligne{struck > 1 ? 's' : ''} rayée{struck > 1 ? 's' : ''} (masquée{struck > 1 ? 's' : ''} à l'impression)
                                        </Badge>
                                      )}
                                    </>
                                  );
                                })() : (
                                  <Badge className={`text-xs ${
                                    expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                    expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                    expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-slate-500/20 text-slate-400'
                                  }`}>{expense.category}</Badge>
                                )}
                                <span className="text-white font-medium">{expense.description}</span>
                                {expense.source === 'appro_manager' && (
                                  <Badge className="text-[10px] bg-cyan-500/30 text-cyan-200 border border-cyan-500/40" title="Issu d'Appro Manager">📋 Appro</Badge>
                                )}
                                {expense.source === 'receipt_scan' && (
                                  <Badge className="text-[10px] bg-amber-500/30 text-amber-200 border border-amber-500/40" title="Scanné via reçu">🔍 Scan</Badge>
                                )}
                                {expense.category === 'paiement' && expense.is_paid && (
                                  <Badge className="text-xs bg-amber-500/30 text-amber-300 border border-amber-500/50" data-testid={`paid-badge-${expense.id}`}>
                                    💰 Payé
                                  </Badge>
                                )}
                              </div>
                              {/* Show quantity and unit price for single items */}
                              {!expense.is_group && (
                                <div className="text-slate-300 text-sm mt-1">
                                  <span className="text-slate-400">Qté:</span> <span className="font-bold">{expense.quantity || 1}</span>
                                  <span className="mx-2">×</span>
                                  <span className="text-slate-400">PU:</span> <span className="font-bold">{formatPrice(expense.unit_price || expense.amount)} F</span>
                                </div>
                              )}
                              <p className="text-green-400 font-bold text-lg">{formatPrice(expense.amount)} F</p>
                              {expense.supplier && <p className="text-slate-500 text-sm">Fournisseur: {expense.supplier}</p>}
                              {expense.planned_date && <p className="text-slate-500 text-sm">Prévu le: {expense.planned_date}</p>}
                              <p className="text-slate-500 text-xs">Approuvé par: {expense.approved_by}</p>
                              {/* === Trace d'audit (corrections admin) === */}
                              {expense.is_group && expense.original_items && (() => {
                                const audit = computeAuditTrail(expense);
                                if (!audit.hasChanges) return null;
                                const showDetails = currentUser?.role === 'admin';
                                return (
                                  <details className="mt-2 bg-slate-800/40 border border-slate-600/40 rounded p-2" data-testid={`audit-trail-${expense.id}`}>
                                    <summary className="cursor-pointer text-xs text-amber-200 font-semibold flex items-center gap-2">
                                      📜 Liste corrigée par {expense.approved_by || 'Admin'}
                                      <span className="text-slate-400 font-normal">
                                        ({audit.added.length > 0 && `+${audit.added.length} ajoutée${audit.added.length > 1 ? 's' : ''}`}
                                        {audit.removed.length > 0 && (audit.added.length ? ', ' : '') + `−${audit.removed.length} supprimée${audit.removed.length > 1 ? 's' : ''}`}
                                        {audit.struck.length > 0 && ((audit.added.length || audit.removed.length) ? ', ' : '') + `${audit.struck.length} rayée${audit.struck.length > 1 ? 's' : ''}`}
                                        {audit.modified.length > 0 && ((audit.added.length || audit.removed.length || audit.struck.length) ? ', ' : '') + `${audit.modified.length} modifiée${audit.modified.length > 1 ? 's' : ''}`})
                                      </span>
                                    </summary>
                                    <div className="mt-2 space-y-1.5 text-xs">
                                      {audit.added.map((it, i) => (
                                        <div key={`a-${i}`} className="flex items-start gap-2 bg-emerald-900/20 border border-emerald-500/30 rounded px-2 py-1">
                                          <span className="text-emerald-400 font-bold shrink-0">＋</span>
                                          <span className="text-emerald-200">
                                            <strong>Ajoutée :</strong> {it.description}
                                            <span className="text-slate-400"> — {it.quantity} × {formatPrice(it.unit_price)} F = {formatPrice(it.amount)} F</span>
                                          </span>
                                        </div>
                                      ))}
                                      {audit.removed.map((it, i) => (
                                        <div key={`r-${i}`} className="flex items-start gap-2 bg-rose-900/20 border border-rose-500/30 rounded px-2 py-1">
                                          <span className="text-rose-400 font-bold shrink-0">−</span>
                                          <span className="text-rose-200">
                                            <strong>Supprimée :</strong> {it.description}
                                            <span className="text-slate-400"> — {it.quantity} × {formatPrice(it.unit_price)} F = {formatPrice(it.amount)} F</span>
                                          </span>
                                        </div>
                                      ))}
                                      {audit.struck.map((it, i) => (
                                        <div key={`s-${i}`} className="flex items-start gap-2 bg-red-900/20 border border-red-500/30 rounded px-2 py-1">
                                          <span className="text-red-400 font-bold shrink-0">🚫</span>
                                          <span className="text-red-200">
                                            <strong>Rayée :</strong> {it.description}
                                            {showDetails && (
                                              <span className="text-slate-400"> — motif : <em>{STRIKE_LABEL[it.strike_reason] || it.strike_reason || '—'}</em>{it.strike_note ? ` (${it.strike_note})` : ''}</span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                      {audit.modified.map((m, i) => (
                                        <div key={`m-${i}`} className="flex items-start gap-2 bg-blue-900/20 border border-blue-500/30 rounded px-2 py-1">
                                          <span className="text-blue-400 font-bold shrink-0">✎</span>
                                          <span className="text-blue-200">
                                            <strong>Modifiée :</strong> {m.current.description}
                                            <span className="text-slate-400"> — </span>
                                            {m.qtyChanged && (
                                              <span className="text-slate-300">qté <s className="text-slate-500">{m.original.quantity}</s> → <strong>{m.current.quantity}</strong></span>
                                            )}
                                            {m.qtyChanged && m.puChanged && <span className="text-slate-500"> · </span>}
                                            {m.puChanged && (
                                              <span className="text-slate-300">PU <s className="text-slate-500">{formatPrice(m.original.unit_price)} F</s> → <strong>{formatPrice(m.current.unit_price)} F</strong></span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                );
                              })()}
                              {/* Badges d'analyse admin (doublons, stock, trésorerie) */}
                              {currentUser?.role === 'admin' && expenseAnalyses[expense.id] && (
                                <div className="mt-2">
                                  <ExpenseAnalysisBadges analysis={expenseAnalyses[expense.id]} />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 flex-wrap shrink-0">
                              {/* Week assignment button */}
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setExpenseToAssign(expense);
                                  setShowWeekAssignModal(true);
                                }}
                                className={`border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 ${expense.assigned_week ? 'bg-cyan-500/20' : ''}`}
                              >
                                <Calendar className="w-4 h-4 mr-1" />
                                {expense.assigned_week ? format(new Date(expense.assigned_week), "dd/MM") : 'Semaine'}
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => printExpensePDF(expense)}
                                className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                              >
                                <Printer className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                              {(currentUser?.role === 'manager' || currentUser?.role === 'admin') && expense.category === 'paiement' && (
                                <Button
                                  size="sm"
                                  variant={expense.is_paid ? "default" : "outline"}
                                  onClick={() => {
                                    const willPay = !expense.is_paid;
                                    const msg = willPay
                                      ? `Confirmer le paiement de :\n\n${expense.description}\nMontant : ${formatPrice(expense.amount)} F`
                                      : `Marquer cette prestation comme NON payée ?`;
                                    if (window.confirm(msg)) {
                                      updateExpense(expense.id, {
                                        is_paid: willPay,
                                        paid_at: willPay ? new Date().toISOString() : null,
                                        paid_by: willPay ? (currentUser?.username || currentUser?.role || '') : null,
                                      });
                                    }
                                  }}
                                  className={expense.is_paid
                                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                                    : "border-amber-500/50 text-amber-400 hover:bg-amber-500/20"}
                                  data-testid={`mark-paid-${expense.id}`}
                                  title={expense.is_paid ? `Payé le ${expense.paid_at?.slice(0,10) || '—'} par ${expense.paid_by || '—'}` : "Marquer la prestation comme payée"}
                                >
                                  <Wallet className="w-4 h-4 mr-1" />
                                  {expense.is_paid ? "Payé ✓" : "Payé"}
                                </Button>
                              )}
                              {(currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
                                <Button 
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Confirmez-vous avoir bien payé/réceptionné cet achat ?\n\n${expense.description}\nMontant : ${formatPrice(expense.amount)} F\n\nLe stock sera automatiquement mis à jour.`)) {
                                      updateExpense(expense.id, { status: "completed" });
                                    }
                                  }}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`mark-completed-${expense.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Acheté
                                </Button>
                              )}
                              {currentUser?.role === 'admin' && (
                                <Button 
                                  size="sm"
                                  onClick={() => convertExpenseToPO(expense)}
                                  disabled={!!expense.converted_to_po_id}
                                  className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                                  data-testid={`convert-po-${expense.id}`}
                                >
                                  <Truck className="w-4 h-4 mr-1" />
                                  {expense.converted_to_po_id ? `BC ${expense.converted_to_po_number || ''}` : 'Convertir en BC'}
                                </Button>
                              )}
                              {currentUser?.role === 'admin' && (
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteExpense(expense.id)}
                                  className="border-red-700/50 text-red-500 hover:bg-red-700/20"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Supprimer
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Funding source control (admin only) — also visible when no account exists yet */}
                          {currentUser?.role === 'admin' && (
                            <div className="mt-2 flex items-center gap-2 bg-cyan-900/10 border border-cyan-500/20 rounded px-2 py-1.5 flex-wrap">
                              <span className="text-[11px] text-cyan-300 shrink-0">💰 Payé depuis :</span>
                              <select
                                value={expense.funded_by_account_id || ""}
                                onChange={(e) => allocateExpenseToAccount(expense, e.target.value, expense.funded_affects_ca !== false)}
                                className="flex-1 bg-slate-800/60 border border-slate-600 text-white text-xs rounded px-2 py-1 min-w-0"
                                data-testid={`funding-source-${expense.id}`}
                              >
                                <option value="">Recettes de la caisse</option>
                                {availableAccounts && availableAccounts.map((acc) => (
                                  <option key={acc.id} value={acc.id}>
                                    📒 {acc.name} — Dispo : {formatPrice(acc.balance_available || 0)} F
                                  </option>
                                ))}
                                <option value="__create_new__" className="text-emerald-300 font-semibold">
                                  ➕ Créer un nouveau compte courant ({formatPrice(expense.amount)} F)
                                </option>
                              </select>
                              {expense.funded_by_account_name && (
                                <Badge className="bg-cyan-500/30 text-cyan-200 text-[10px] shrink-0">
                                  imputé : {expense.funded_by_account_name}
                                </Badge>
                              )}
                            </div>
                          )}
                          {/* Show sub-items for grouped lists */}
                          {expense.is_group && expense.items && expense.items.length > 0 && (
                            <div className="bg-slate-800/50 rounded p-2 mt-1">
                              <p className="text-xs text-slate-400 mb-2">📋 Détails de la liste:</p>
                              <div className="space-y-1">
                                {sortItems(expense.items).map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-700/50 pb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-500">{idx + 1}.</span>
                                      <Badge className={`text-xs ${
                                        item.category === 'cuisine' ? 'bg-green-500/10 text-green-500' :
                                        item.category === 'bar' ? 'bg-orange-500/10 text-orange-500' :
                                        item.category === 'paiement' ? 'bg-blue-500/10 text-blue-500' :
                                        'bg-slate-500/10 text-slate-500'
                                      }`}>{item.category}</Badge>
                                      <span className="text-white">{item.description}</span>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-slate-400 text-xs">{item.quantity} × {formatPrice(item.unit_price)} = </span>
                                      <span className="text-green-400 font-bold">{formatPrice(item.amount)} F</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                );
              })()}

              {/* === ACHATS MANAGER — Uniquement les produits achetés (Admin only) === */}
              {achatsSubView === 'achete' && isAdminUser && (
                <AchatsManagerPanels
                  subView={achatsSubView}
                  expenses={sortExpenses(expenses)}
                  formatPrice={formatPrice}
                  deleteExpense={deleteExpense}
                  onChanged={fetchExpenses}
                  currentUser={currentUser}
                />
              )}

              {/* Completed expenses (Achats terminés — affichés sous Validés pour admin, ou via vue 'termines' dédiée) */}
              {(achatsSubView === 'termines' || achatsSubView === 'valides') && isAdminUser && (() => {
                const finishedList = sortExpenses(expenses.filter(isFinished));
                return finishedList.length > 0 ? (
                  <Card className="bg-gradient-to-br from-slate-800/40 to-slate-900/30 border-slate-600/50" data-testid="completed-expenses-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-slate-200 flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Achats &amp; prestations terminés
                          <Badge className="bg-slate-500/30 text-slate-200 ml-2">
                            {finishedList.length}
                          </Badge>
                          <Badge className="bg-emerald-500/30 text-emerald-300">
                            Total: {formatPrice(finishedList.reduce((s, e) => s + (e.amount || 0), 0))} F
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={printCompletedExpensesTicket}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                            data-testid="print-completed-ticket-btn"
                          >
                            <Receipt className="w-4 h-4 mr-1" />
                            Ticket 80mm
                          </Button>
                          <Button
                            size="sm"
                            onClick={printAllCompletedExpenses}
                            className="bg-slate-600 hover:bg-slate-700"
                            data-testid="print-completed-a4-btn"
                          >
                            <Printer className="w-4 h-4 mr-1" />
                            Imprimer A4
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                      {finishedList.map(expense => (
                        <div
                          key={expense.id}
                          className={`bg-slate-700/20 rounded-lg p-3 border ${selectionMode && selectedIds.has(expense.id) ? 'border-purple-500/70 ring-1 ring-purple-500/40' : 'border-slate-600/30'} relative`}
                          data-testid={`completed-expense-${expense.id}`}
                          onClick={selectionMode ? () => toggleSelection(expense.id) : undefined}
                          style={selectionMode ? { cursor: 'pointer' } : undefined}
                        >
                          {selectionMode && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(expense.id)}
                              onChange={() => toggleSelection(expense.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute top-2 left-2 w-4 h-4 accent-purple-500 cursor-pointer z-10"
                              data-testid={`select-expense-${expense.id}`}
                            />
                          )}
                          {expense.assigned_date && (
                            <span className="absolute top-2 right-2 text-[10px] bg-purple-500/30 text-purple-200 border border-purple-500/50 rounded-full px-2 py-0.5 z-10" data-testid={`assigned-badge-${expense.id}`}>
                              📌 {expense.assignment_precision === 'month' ? `Rattaché à ${expense.assigned_date.slice(0,7)}` : `Rattaché au ${expense.assigned_date}`}
                            </span>
                          )}
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {expense.is_group ? (
                                    <Badge className="text-xs bg-indigo-500/30 text-indigo-300">📦 Liste ({expense.items?.length || 0} articles)</Badge>
                                  ) : (
                                    <Badge className={`text-xs ${
                                      expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                      expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                      expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                      'bg-slate-500/20 text-slate-400'
                                    }`}>{expense.category}</Badge>
                                  )}
                                  <span className="text-white font-medium">{expense.description}</span>
                                </div>
                                {!expense.is_group && (
                                  <div className="text-slate-300 text-sm mt-1">
                                    <span className="text-slate-400">Qté:</span> <span className="font-bold">{expense.quantity || 1}</span>
                                    <span className="mx-2">×</span>
                                    <span className="text-slate-400">PU:</span> <span className="font-bold">{formatPrice(expense.unit_price || expense.amount)} F</span>
                                  </div>
                                )}
                                <p className="text-emerald-400 font-bold text-lg">{formatPrice(expense.amount)} F</p>
                                {expense.supplier && <p className="text-slate-500 text-sm">Fournisseur: {expense.supplier}</p>}
                                <p className="text-slate-500 text-xs">Terminé le {expense.completed_at?.slice(0, 10) || '—'}</p>
                              </div>
                              <div className="flex gap-2 flex-wrap shrink-0">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => printSingleExpenseTicket(expense)}
                                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                  data-testid={`print-ticket-${expense.id}`}
                                >
                                  <Receipt className="w-4 h-4 mr-1" />
                                  Ticket 80mm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => printExpensePDF(expense)}
                                  className="border-slate-500/50 text-slate-300 hover:bg-slate-500/20"
                                  data-testid={`print-pdf-${expense.id}`}
                                >
                                  <Printer className="w-4 h-4 mr-1" />
                                  PDF
                                </Button>
                                {currentUser?.role === 'admin' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteExpense(expense.id)}
                                    className="border-red-700/50 text-red-500 hover:bg-red-700/20"
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Supprimer
                                  </Button>
                                )}
                              </div>
                            </div>
                            {/* Funding source control (admin only) — also on completed */}
                            {currentUser?.role === 'admin' && (
                              <div className="mt-2 flex items-center gap-2 bg-cyan-900/10 border border-cyan-500/20 rounded px-2 py-1.5 flex-wrap">
                                <span className="text-[11px] text-cyan-300 shrink-0">💰 Payé depuis :</span>
                                <select
                                  value={expense.funded_by_account_id || ""}
                                  onChange={(e) => allocateExpenseToAccount(expense, e.target.value, expense.funded_affects_ca !== false)}
                                  className="flex-1 min-w-[160px] bg-slate-800/60 border border-slate-600 text-white text-xs rounded px-2 py-1"
                                  data-testid={`funding-source-completed-${expense.id}`}
                                >
                                  <option value="">Recettes de la caisse</option>
                                  {availableAccounts && availableAccounts.map((acc) => (
                                    <option key={acc.id} value={acc.id}>
                                      📒 {acc.name} — Dispo : {formatPrice(acc.balance_available || 0)} F
                                    </option>
                                  ))}
                                  <option value="__create_new__" className="text-emerald-300 font-semibold">
                                    ➕ Créer un nouveau compte courant ({formatPrice(expense.amount)} F)
                                  </option>
                                </select>
                                {expense.funded_by_account_name && (
                                  <Badge className="bg-cyan-500/30 text-cyan-200 text-[10px] shrink-0">
                                    imputé : {expense.funded_by_account_name}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {expense.is_group && expense.items && expense.items.length > 0 && (
                              <div className="bg-slate-800/40 rounded p-2 mt-1">
                                <p className="text-xs text-slate-400 mb-2">📋 Détails de la liste:</p>
                                <div className="space-y-1">
                                  {sortItems(expense.items).map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-700/50 pb-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-500">{idx + 1}.</span>
                                        <Badge className={`text-xs ${
                                          item.category === 'cuisine' ? 'bg-green-500/10 text-green-500' :
                                          item.category === 'bar' ? 'bg-orange-500/10 text-orange-500' :
                                          item.category === 'paiement' ? 'bg-blue-500/10 text-blue-500' :
                                          'bg-slate-500/10 text-slate-500'
                                        }`}>{item.category}</Badge>
                                        <span className="text-white">{item.description}</span>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-slate-400 text-xs">{item.quantity} × {formatPrice(item.unit_price)} = </span>
                                        <span className="text-emerald-400 font-bold">{formatPrice(item.amount)} F</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-slate-800/30 border-slate-700">
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-500">Aucun achat ni prestation terminé</p>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Fin du bloc "Vue : cartes" — la suite (Rejetés, Mes demandes, modals) reste toujours visible */}
              </>)}

              {/* Rejected expenses */}
              {achatsSubView === 'rejetes' && expenses.filter(e => e.status === 'rejected').length > 0 && (
                <Card className="bg-rose-900/20 border-rose-500/30" data-testid="rejected-expenses-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-rose-300 flex items-center gap-2">
                      <X className="w-5 h-5" />
                      Achats rejetés
                      <Badge className="bg-rose-500/30 text-rose-200 ml-2">
                        {expenses.filter(e => e.status === 'rejected').length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                    {sortExpenses(expenses.filter(e => e.status === 'rejected')).map(expense => (
                      <div key={expense.id} className="flex items-center justify-between gap-2 bg-rose-800/10 rounded-lg p-2 border border-rose-500/20" data-testid={`rejected-expense-${expense.id}`}>
                        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                          <Badge className={`text-xs ${
                            expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                            expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                            expense.category === 'jeux' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>{expense.category}</Badge>
                          <span className="text-slate-200 text-sm truncate">{expense.description}</span>
                          {expense.admin_notes && (
                            <span className="text-rose-300 text-xs italic">
                              — {expense.admin_notes}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-rose-300 font-semibold text-sm">{formatPrice(expense.amount)} F</span>
                          {currentUser?.role === 'admin' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => deleteExpense(expense.id)}
                              className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/20">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* ============================================================
                   MES DEMANDES (Admin only) — demandes créées par l'admin
                  ============================================================ */}
              {achatsSubView === 'mes_demandes' && currentUser?.role === 'admin' && (() => {
                const me = currentUser?.full_name || currentUser?.username || '';
                const myExpenses = sortExpenses(expenses.filter(e =>
                  e.requested_by === me || e.requested_by === currentUser?.username
                ));
                if (myExpenses.length === 0) {
                  return (
                    <Card className="bg-slate-800/30 border-slate-700" data-testid="my-requests-empty">
                      <CardContent className="py-12 text-center">
                        <ShoppingCart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-500">Vous n'avez pas encore créé de demande d'achat</p>
                        <p className="text-slate-600 text-xs mt-1">
                          Cliquez sur <strong className="text-purple-300">"Nouvelle demande d'achat"</strong> en haut pour en créer une.
                        </p>
                      </CardContent>
                    </Card>
                  );
                }
                const STATUS_META = {
                  pending:            { label: 'En attente',     color: 'bg-amber-500/20 text-amber-300' },
                  admin_review:       { label: 'En correction',  color: 'bg-orange-500/20 text-orange-300' },
                  revision_requested: { label: 'À réviser',      color: 'bg-rose-500/20 text-rose-300' },
                  approved:           { label: 'Validée',        color: 'bg-emerald-500/20 text-emerald-300' },
                  completed:          { label: 'Terminée',       color: 'bg-blue-500/20 text-blue-300' },
                  rejected:           { label: 'Rejetée',        color: 'bg-slate-500/20 text-slate-400' },
                };
                return (
                  <Card className="bg-gradient-to-br from-indigo-900/30 to-purple-900/20 border-indigo-500/40" data-testid="my-requests-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-indigo-200 flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" />
                        MES DEMANDES D'ACHAT
                        <Badge className="bg-indigo-500/30 text-indigo-200 ml-2">
                          {myExpenses.length}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-900/40 text-slate-400 text-xs uppercase">
                            <tr>
                              <th className="p-2 text-left">Date</th>
                              <th className="p-2 text-left">Catégorie</th>
                              <th className="p-2 text-left">Description</th>
                              <th className="p-2 text-right">Montant</th>
                              <th className="p-2 text-center">Statut</th>
                              <th className="p-2 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myExpenses.map(expense => {
                              const meta = STATUS_META[expense.status] || { label: expense.status, color: 'bg-slate-500/20 text-slate-400' };
                              return (
                                <tr key={expense.id} className="border-t border-slate-700/50 hover:bg-slate-800/30 group">
                                  <td className="p-2 text-slate-400 text-xs">
                                    {expense.created_at ? format(parseISO(expense.created_at), 'dd/MM/yyyy', { locale: fr }) : '—'}
                                  </td>
                                  <td className="p-2">
                                    <Badge className={`text-xs ${
                                      expense.category === 'cuisine' ? 'bg-green-500/20 text-green-400' :
                                      expense.category === 'bar' ? 'bg-orange-500/20 text-orange-400' :
                                      expense.category === 'jeux' ? 'bg-blue-500/20 text-blue-400' :
                                      expense.category === 'paiement' ? 'bg-blue-500/20 text-blue-400' :
                                      'bg-slate-500/20 text-slate-400'
                                    }`}>{expense.category}</Badge>
                                  </td>
                                  <td className="p-2 text-white">
                                    <span className="flex items-center gap-2 flex-wrap">
                                      {expense.is_group
                                        ? <span className="font-semibold">{expense.description} ({expense.items?.length || 0} articles)</span>
                                        : expense.description}
                                      <TypeDestBadges expense={expense} />
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-bold text-purple-300">{formatPrice(expense.amount)} F</td>
                                  <td className="p-2 text-center">
                                    <Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                                  </td>
                                  <td className="p-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm" variant="ghost"
                                        onClick={() => openExpenseForEdit ? openExpenseForEdit(expense) : null}
                                        className="h-7 w-7 p-0 text-indigo-400 hover:bg-indigo-500/20"
                                        title="Modifier / Réviser"
                                        data-testid={`my-request-edit-${expense.id}`}
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm" variant="ghost"
                                        onClick={() => deleteExpense(expense.id)}
                                        className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/20"
                                        title="Supprimer"
                                        data-testid={`my-request-delete-${expense.id}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {expenses.length === 0 && (
                <Card className="bg-slate-800/30 border-slate-700">
                  <CardContent className="py-12 text-center">
                    <ShoppingCart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500">Aucune demande d'achat</p>
                  </CardContent>
                </Card>
              )}
              </>)}

              {/* Modal : Achat Boissons (liaison directe au stock) */}
              <DrinkPurchaseDialog
                open={showDrinksPurchase}
                onClose={() => setShowDrinksPurchase(false)}
                currentUser={currentUser}
                onCreated={() => { if (fetchExpenses) fetchExpenses(); }}
              />

              {/* Dialog : Rattachement à une période (sélection multiple) */}
              <AssignDateDialog
                open={showAssignDateDialog}
                onClose={() => setShowAssignDateDialog(false)}
                collection="expenses"
                ids={Array.from(selectedIds)}
                currentUser={currentUser}
                onDone={() => { clearSelection(); setSelectionMode(false); if (fetchExpenses) fetchExpenses(); }}
              />
            </div>

  );
};

export default AchatsTab;
